import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../contexts/JobContext';
import { Mic, FileText, Wand2, Upload, Link, Link2, Paperclip, Trash2, Loader2, Globe, Check, RefreshCw, Save, History, FileUp, Type, Radio, ArrowUp, ArrowDown, GripVertical, Pencil, ExternalLink, CheckCircle, AlertCircle, AlertTriangle, Clock, Code, Eye, Settings2, X, Sparkles, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import SimpleEditor from '../components/SimpleEditor';
import ServiceLLMToggle from '../components/ServiceLLMToggle';
// Composants extraits pour améliorer la maintenabilité
import { 
  formatElapsedTime, 
  RegenerationPanel, 
  VersionSelector,
  SiteSelector,
  ImageModeSelector,
  ExtraSlots,
  GeneratedArticleCard
} from '../components/CreateArticle';

// URL de base
const API = process.env.REACT_APP_BACKEND_URL;
const STATIC_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateArticle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();  // Pour isoler le sessionStorage par utilisateur
  const { registerJob, subscribeToJob, getJob, restoreActiveJobs, activeJobs } = useJobs();
  const preselectedSiteId = location.state?.selectedSiteId;
  const preselectedSiteName = location.state?.siteName;
  const multiSiteMode = location.state?.multiSiteMode;
  
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [orderedSites, setOrderedSites] = useState([]); // Sites dans l'ordre de génération
  const [preselectedSite, setPreselectedSite] = useState(null);
  
  // Mode de saisie : 'dictee', 'document', 'texte'
  const [inputMode, setInputMode] = useState('dictee');
  
  // Contenu principal selon le mode
  const [dicteeText, setDicteeText] = useState(''); // Mode dictée
  const [pastedText, setPastedText] = useState(''); // Mode texte collé
  const [uploadedDocument, setUploadedDocument] = useState(null); // Mode document
  
  // Type de contenu : 'article', 'tribune', 'avis_expert', 'edito'
  const [contentType, setContentType] = useState('article');
  
  // Compléments (angle, sources) - pour mode article
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [additionalUrls, setAdditionalUrls] = useState('');
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [scrapeAlert, setScrapeAlert] = useState(null); // {jobId} quand le scraping a échoué
  const [llmErrorAlert, setLlmErrorAlert] = useState(null); // {sites, message} quand le LLM bloque la génération (filtre de contenu)
  const [jobsActivity, setJobsActivity] = useState(null); // {active_jobs, active_articles, recently_completed}
  const [generationLock, setGenerationLock] = useState({ locked: false }); // Nouveau système de verrou MongoDB
  
  // Refs pour le polling (évite les re-renders en cascade)
  const activityPollRef = useRef(null);
  const activityPollRateRef = useRef(10000);
  
  // Prompts sauvegardés
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showPrompts, setShowPrompts] = useState(false);
  
  // Prompts par site (override ponctuel)
  const [sitePrompts, setSitePrompts] = useState({});
  // Instructions de réécriture par site (optionnel, pour l'étape d'amélioration)
  const [siteRewritePrompts, setSiteRewritePrompts] = useState({});
  // Toggle réglages avancés par site
  const [showAdvanced, setShowAdvanced] = useState({});
  
  // Option image : "none", "ai", "periscope", "ai+periscope"
  const [imageMode, setImageMode] = useState('none');
  const [imageSearchKeyword, setImageSearchKeyword] = useState('');  // Mot-clé de recherche Periscope
  const [autoEnrich, setAutoEnrich] = useState(true);  // Recherche automatique d'actualités - OBLIGATOIRE par défaut
  const [searchOverride, setSearchOverride] = useState(false);  // Bypasser l'obligation de recherche internet
  const [queryOverride, setQueryOverride] = useState(false);  // Bypasser l'obligation de requête personnalisée
  const [enrichLanguages, setEnrichLanguages] = useState(['fr']);  // Langues ciblées pour l'enrichissement
  const [enrichQuery, setEnrichQuery] = useState('');  // Requête personnalisée - OBLIGATOIRE par défaut
  
  // Titres imposés par l'auteur (un par site) - OBLIGATOIRE par défaut
  const [siteTitles, setSiteTitles] = useState({});
  const [titlesOverride, setTitlesOverride] = useState(false);  // Bypasser l'obligation de titres utilisateur
  const [autoSuggestTitles, setAutoSuggestTitles] = useState(true);  // Auto-suggestion IA des titres
  const [suggestingTitles, setSuggestingTitles] = useState(false);  // État de chargement suggestions
  
  // Articles supplémentaires (hors sites paramétrés) - max 3
  const [extraSlots, setExtraSlots] = useState([]);  // [{id, label, prompt, title}]
  const [showExtraSlots, setShowExtraSlots] = useState(false);
  
  // SEO - Mots-clés
  const [primarySeoKeyword, setPrimarySeoKeyword] = useState('');  // Mot-clé SEO principal
  const [secondaryKeywords, setSecondaryKeywords] = useState('');  // Mots-clés secondaires (séparés par virgule)
  const [seoOverride, setSeoOverride] = useState(false);  // Bypasser l'obligation SEO
  
  // Délai entre publications multi-sites (en minutes)
  const [publishDelay, setPublishDelay] = useState(0);
  
  // Mode Cluster SEO (maillage interne entre articles)
  const [clusterMode, setClusterMode] = useState(false);
  const [clusterLinks, setClusterLinks] = useState({});  // {siteId: [targetSiteId1, targetSiteId2, ...]}
  const [showClusterWarning, setShowClusterWarning] = useState(false);  // Alerte activation cluster
  
  // Réécriture anti-watermark - utilise GPT-5.5 fixe - ACTIVÉE PAR DÉFAUT
  const [enableOpenaiRewrite, setEnableOpenaiRewrite] = useState(true);
  
  // Historique des 3 dernières créations
  const [creationHistory, setCreationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Job asynchrone
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  
  // Timer pour afficher le temps écoulé et prouver que le processus tourne
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastPollTime, setLastPollTime] = useState(null);
  
  // État pour les articles générés
  const [generatedArticles, setGeneratedArticles] = useState(null);
  const [totalCost, setTotalCost] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedArticles, setPublishedArticles] = useState({});
  
  // Upload d'image personnalisée par site
  const [uploadingCustomImage, setUploadingCustomImage] = useState({}); // {siteId: true/false}

  // États pour la régénération
  const [showRegenPanel, setShowRegenPanel] = useState(false); // Fermé par défaut, déroulable
  const [regenSites, setRegenSites] = useState({}); // {siteId: {selected: bool, angle: string, title: string}}
  const [regenerating, setRegenerating] = useState(false);
  const [regenJobId, setRegenJobId] = useState(null);
  
  // Flag pour éviter les toasts multiples à la fin de la génération
  const completionNotifiedRef = useRef(false);

  // Clé sessionStorage unique par utilisateur (évite les conflits multi-onglets/utilisateurs)
  const storageKey = user?.id ? `autoedito_${user.id}_generated_articles` : 'autoedito_generated_articles';

  // Restaurer les articles depuis sessionStorage au montage
  useEffect(() => {
    if (!user?.id) return;  // Attendre que l'utilisateur soit chargé
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setGeneratedArticles(parsed.articles);
        setTotalCost(parsed.totalCost);
        if (parsed.publishedArticles) setPublishedArticles(parsed.publishedArticles);
      } catch (e) { /* ignore */ }
    }
  }, [user?.id, storageKey]);


  // Persister les articles generes dans sessionStorage
  useEffect(() => {
    if (generatedArticles && user?.id) {
      sessionStorage.setItem(storageKey, JSON.stringify({
        articles: generatedArticles,
        totalCost,
        publishedArticles,
      }));
    }
  }, [generatedArticles, totalCost, publishedArticles, user?.id, storageKey]);

  // === Snapshot du dernier job complet (localStorage, survit aux onglets/sessions) ===
  const [lastJobSnapshot, setLastJobSnapshot] = useState(null);
  const snapshotKey = user?.id ? `autoedito_${user.id}_last_job_snapshot` : null;

  // Charger le snapshot au montage
  useEffect(() => {
    if (!snapshotKey) return;
    try {
      const saved = localStorage.getItem(snapshotKey);
      if (saved) setLastJobSnapshot(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, [snapshotKey]);

  // Sauvegarder automatiquement le snapshot à la fin de la génération OU régénération
  const prevGeneratingRef = useRef(false);
  const prevRegeneratingRef = useRef(false);
  
  useEffect(() => {
    if (!snapshotKey) { 
      prevGeneratingRef.current = generating; 
      prevRegeneratingRef.current = regenerating;
      return; 
    }
    const justFinishedGenerating = prevGeneratingRef.current && !generating;
    const justFinishedRegenerating = prevRegeneratingRef.current && !regenerating;
    
    if ((justFinishedGenerating || justFinishedRegenerating) && generatedArticles && Object.keys(generatedArticles).length > 0) {
      const snapshot = {
        saved_at: new Date().toISOString(),
        articles: generatedArticles,
        totalCost,
        publishedArticles,
        jobStatus,
        currentJobId,
        form: {
          dicteeText,
          pastedText,
          additionalNotes,
          additionalUrls,
          selectedSites,
          sitePrompts,
          siteRewritePrompts,
          siteTitles,
          primarySeoKeyword,
          secondaryKeywords,
          imageMode,
          imageSearchKeyword,
          autoEnrich,
          enrichLanguages,
          enrichQuery,
          contentType,
          inputMode,
        },
      };
      try {
        localStorage.setItem(snapshotKey, JSON.stringify(snapshot));
        setLastJobSnapshot(snapshot);
      } catch (e) { /* quota */ }
    }
    prevGeneratingRef.current = generating;
    prevRegeneratingRef.current = regenerating;
  }, [generating, regenerating, generatedArticles]);

  const restoreLastJob = () => {
    if (!lastJobSnapshot) {
      toast.info("Aucun job enregistré");
      return;
    }
    const snap = lastJobSnapshot;
    if (snap.form) {
      setDicteeText(snap.form.dicteeText || '');
      setPastedText(snap.form.pastedText || '');
      setAdditionalNotes(snap.form.additionalNotes || '');
      setAdditionalUrls(snap.form.additionalUrls || '');
      setSelectedSites(snap.form.selectedSites || []);
      setSitePrompts(snap.form.sitePrompts || {});
      setSiteRewritePrompts(snap.form.siteRewritePrompts || {});
      setSiteTitles(snap.form.siteTitles || {});
      setPrimarySeoKeyword(snap.form.primarySeoKeyword || '');
      setSecondaryKeywords(snap.form.secondaryKeywords || '');
      setImageMode(snap.form.imageMode || 'none');
      setImageSearchKeyword(snap.form.imageSearchKeyword || '');
      setAutoEnrich(snap.form.autoEnrich !== undefined ? snap.form.autoEnrich : true);
      setEnrichLanguages(snap.form.enrichLanguages?.length ? snap.form.enrichLanguages : ['fr']);
      setEnrichQuery(snap.form.enrichQuery || '');
      setContentType(snap.form.contentType || 'article');
      if (snap.form.inputMode) setInputMode(snap.form.inputMode);
    }
    setGeneratedArticles(snap.articles || {});
    setTotalCost(snap.totalCost ?? null);
    setPublishedArticles(snap.publishedArticles || {});
    setJobStatus(snap.jobStatus || null);
    setCurrentJobId(snap.currentJobId || null);
    setShowHistory(false);
    // Scroll vers la section des articles générés
    setTimeout(() => {
      const el = document.getElementById('generated-articles');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    const when = snap.saved_at ? new Date(snap.saved_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
    toast.success(`Derniers articles restaurés${when ? ` (${when})` : ''}`);
  };

  // Chargement initial des données (une seule fois au montage)
  useEffect(() => {
    loadSites();
    loadPrompts();
    loadCreationHistory();
    
    // Restaurer les jobs actifs depuis le backend via JobContext
    if (user?.id) {
      restoreActiveJobs(user.id);
    }
  }, [user?.id, restoreActiveJobs]);

  const loadCreationHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/creation-history`);
      setCreationHistory(res.data);
    } catch (e) { /* ignore */ }
  };

  // Helper: Vérifier si un site est éligible au cluster (catégorie 1 = vert)
  const isClusterEligible = (site) => {
    if (!site) return false;
    const isConnected = site.is_connected !== false;
    const isConfigured = (site.pitch?.trim().length > 0) || (site.default_prompt?.trim().length > 0);
    return isConnected && isConfigured;
  };

  // Sites éligibles au cluster parmi les sites sélectionnés
  const eligibleClusterSites = useMemo(() => {
    return orderedSites.filter(site => isClusterEligible(site));
  }, [orderedSites]);

  // Activer/désactiver le mode cluster avec alerte
  const handleClusterToggle = () => {
    if (!clusterMode) {
      // Activation : afficher l'alerte
      setShowClusterWarning(true);
    } else {
      // Désactivation : reset les liens
      setClusterMode(false);
      setClusterLinks({});
    }
  };

  // Confirmer l'activation du cluster après l'alerte
  const confirmClusterActivation = () => {
    setClusterMode(true);
    setShowClusterWarning(false);
    // Par défaut, aucun lien sélectionné
    setClusterLinks({});
  };

  // Mettre à jour les liens d'un site source
  const updateClusterLinks = (sourceSiteId, targetSiteIds) => {
    setClusterLinks(prev => ({
      ...prev,
      [sourceSiteId]: targetSiteIds
    }));
  };

  // Compter le nombre total de liens configurés
  const totalClusterLinks = useMemo(() => {
    return Object.values(clusterLinks).reduce((sum, targets) => sum + targets.length, 0);
  }, [clusterLinks]);

  // Polling activité inter-comptes (toutes les 10s) + nouveau verrou MongoDB
  useEffect(() => {
    const fetchActivity = async () => {
      try {
        // Ancien système (pour rétrocompatibilité)
        const activityRes = await axios.get(`${API}/api/jobs/activity`);
        setJobsActivity(activityRes.data);
        
        // Nouveau système de verrou MongoDB (source de vérité)
        const lockRes = await axios.get(`${API}/api/generation/status`);
        setGenerationLock(lockRes.data || { locked: false });
      } catch (e) { /* ignore */ }
    };
    fetchActivity();
    // Setup initial polling
    activityPollRef.current = setInterval(fetchActivity, 10000);
    activityPollRateRef.current = 10000;
    
    return () => {
      if (activityPollRef.current) {
        clearInterval(activityPollRef.current);
      }
    };
  }, []);

  // Ajuster la fréquence de polling quand generationLock.locked change
  useEffect(() => {
    const targetRate = generationLock.locked ? 5000 : 10000;
    
    if (activityPollRateRef.current !== targetRate) {
      const fetchActivity = async () => {
        try {
          const activityRes = await axios.get(`${API}/api/jobs/activity`);
          setJobsActivity(activityRes.data);
          const lockRes = await axios.get(`${API}/api/generation/status`);
          setGenerationLock(lockRes.data || { locked: false });
        } catch (e) { /* ignore */ }
      };
      
      if (activityPollRef.current) {
        clearInterval(activityPollRef.current);
      }
      activityPollRef.current = setInterval(fetchActivity, targetRate);
      activityPollRateRef.current = targetRate;
    }
  }, [generationLock.locked]);

  const loadFromHistory = (entry) => {
    const p = entry.params;
    // Restaurer les sites sélectionnés
    if (p.site_ids?.length) setSelectedSites(p.site_ids);
    // Restaurer le contenu (tronqué dans l'historique)
    if (p.transcription) setDicteeText(p.transcription);
    if (p.additional_notes) setAdditionalNotes(p.additional_notes);
    if (p.additional_urls) setAdditionalUrls(p.additional_urls);
    if (p.image_mode) setImageMode(p.image_mode);
    if (p.image_search_keyword) setImageSearchKeyword(p.image_search_keyword);
    if (p.auto_enrich !== undefined) setAutoEnrich(p.auto_enrich);
    if (Array.isArray(p.enrich_languages) && p.enrich_languages.length) setEnrichLanguages(p.enrich_languages);
    if (p.enrich_query) setEnrichQuery(p.enrich_query);
    // content_type toujours "article" par défaut (choix supprimé de l'UI)
    if (p.site_prompts) setSitePrompts(p.site_prompts);
    if (p.site_rewrite_prompts) setSiteRewritePrompts(p.site_rewrite_prompts);
    if (p.site_titles) setSiteTitles(p.site_titles);
    if (p.primary_seo_keyword) setPrimarySeoKeyword(p.primary_seo_keyword);
    if (p.secondary_keywords?.length) setSecondaryKeywords(p.secondary_keywords.join(', '));
    setInputMode('dictee');
    setShowHistory(false);
    toast.success(`Paramètres chargés : ${entry.label}`);
  };

  const loadPrompts = async () => {
    try {
      const response = await axios.get(`${API}/api/prompts`);
      setSavedPrompts(response.data);
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const saveCurrentPrompt = async () => {
    if (!additionalNotes.trim()) {
      toast.error('Rien à sauvegarder');
      return;
    }
    try {
      await axios.post(`${API}/api/prompts?content=${encodeURIComponent(additionalNotes)}`);
      toast.success('Prompt sauvegardé !');
      loadPrompts();
    } catch (error) {
      toast.error('Erreur de sauvegarde');
    }
  };

  const loadPrompt = (prompt) => {
    setAdditionalNotes(prompt.content);
    setShowPrompts(false);
    toast.success('Prompt chargé');
  };

  const deletePrompt = async (promptId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/prompts/${promptId}`);
      loadPrompts();
      toast.success('Supprimé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  useEffect(() => {
    // Single site preselection
    if (preselectedSiteId && sites.length > 0) {
      const site = sites.find(s => s.id === preselectedSiteId);
      if (site) {
        setPreselectedSite(site);
        setSelectedSites([preselectedSiteId]);
        setOrderedSites([site]);
      }
    }
    // Multi-sites mode
    else if (multiSiteMode && sites.length > 0) {
      // Don't preselect anything, let user choose
      setPreselectedSite(null);
    }
  }, [preselectedSiteId, multiSiteMode, sites]);

  // Update orderedSites when selectedSites changes
  useEffect(() => {
    setOrderedSites(prev => {
      // Keep existing order, add new sites at end, remove deselected
      const existingIds = prev.map(s => s.id);
      const newSites = selectedSites
        .filter(id => !existingIds.includes(id))
        .map(id => sites.find(s => s.id === id))
        .filter(Boolean);
      const kept = prev.filter(s => selectedSites.includes(s.id));
      return [...kept, ...newSites];
    });
  }, [selectedSites, sites]);

  // Réordonner les sites
  const moveSiteUp = (index) => {
    if (index === 0) return;
    const newOrdered = [...orderedSites];
    [newOrdered[index - 1], newOrdered[index]] = [newOrdered[index], newOrdered[index - 1]];
    setOrderedSites(newOrdered);
  };

  const moveSiteDown = (index) => {
    if (index === orderedSites.length - 1) return;
    const newOrdered = [...orderedSites];
    [newOrdered[index], newOrdered[index + 1]] = [newOrdered[index + 1], newOrdered[index]];
    setOrderedSites(newOrdered);
  };

  // Suggérer des titres IA pour les sites sélectionnés
  const suggestTitlesForSites = async () => {
    if (!enrichQuery.trim() && !additionalNotes.trim()) {
      toast.error('Veuillez saisir un sujet ou une requête');
      return;
    }
    if (orderedSites.length === 0) {
      toast.error('Sélectionnez au moins un site');
      return;
    }

    setSuggestingTitles(true);
    try {
      const res = await axios.post(`${API}/api/suggest-titles`, {
        query: enrichQuery.trim() || additionalNotes.trim(),
        sites: orderedSites.map(s => ({ id: s.id, name: s.name, pitch: s.pitch || '' })),
        primary_keyword: primarySeoKeyword || null
      });
      
      if (res.data.success && res.data.titles) {
        // Pré-remplir les titres suggérés (sans écraser ceux déjà saisis)
        const newTitles = { ...siteTitles };
        let filled = 0;
        for (const [siteId, title] of Object.entries(res.data.titles)) {
          if (!newTitles[siteId]?.trim() && title) {
            newTitles[siteId] = title;
            filled++;
          }
        }
        setSiteTitles(newTitles);
        toast.success(`${filled} titre${filled > 1 ? 's' : ''} suggéré${filled > 1 ? 's' : ''} par l'IA`);
      }
    } catch (error) {
      console.error('Erreur suggestion titres:', error);
      toast.error('Erreur lors de la suggestion des titres');
    } finally {
      setSuggestingTitles(false);
    }
  };

  // NOTE: Le polling du statut est maintenant géré par le JobContext global
  // Cela évite le chevauchement des requêtes et survit aux changements de page

  const handleScrapeDecision = async (decision) => {
    if (!scrapeAlert?.jobId) return;
    try {
      await axios.post(`${API}/api/jobs/${scrapeAlert.jobId}/scrape-decision`, { decision });
      setScrapeAlert(null);
      if (decision === 'cancel') {
        setGenerating(false);
        setCurrentJobId(null);
        toast.info('Génération annulée');
      }
    } catch (e) {
      console.error('Error sending scrape decision:', e);
    }
  };


  // Synchronisation avec le JobContext pour le polling centralisé
  useEffect(() => {
    if (!currentJobId) return;
    
    // S'abonner aux mises à jour du job via le JobContext
    const unsubscribe = subscribeToJob(currentJobId, (jobData) => {
      setLastPollTime(Date.now());
      setJobStatus(jobData);
      
      // Charger les articles générés progressivement
      if (jobData.sites_status) {
        const loadArticles = async () => {
          const articlesToLoad = [];
          
          for (const site of jobData.sites_status) {
            if (site.article_id && site.status === 'completed') {
              articlesToLoad.push({
                siteId: site.site_id,
                articleId: site.article_id,
                siteName: site.site_name,
                isExtra: site.is_extra || false
              });
            }
          }
          
          // Charger tous les articles en parallèle
          const loadedArticles = await Promise.all(
            articlesToLoad.map(async ({ siteId, articleId, siteName, isExtra }) => {
              try {
                const articleRes = await axios.get(`${API}/api/articles/${articleId}`);
                return {
                  siteId,
                  data: { 
                    ...articleRes.data, 
                    site_name: siteName, 
                    is_extra: isExtra 
                  }
                };
              } catch (e) {
                console.error('Error loading article:', e);
                return null;
              }
            })
          );
          
          // Utiliser une mise à jour fonctionnelle pour éviter les race conditions
          // Cela garantit que chaque mise à jour part de l'état le plus récent
          const validArticles = loadedArticles.filter(a => a !== null);
          if (validArticles.length > 0) {
            setGeneratedArticles(prev => {
              const updated = { ...(prev || {}) };
              for (const { siteId, data } of validArticles) {
                if (!updated[siteId]) {
                  updated[siteId] = data;
                }
              }
              return updated;
            });
          }
        };
        loadArticles();
      }
      
      // Gérer les états finaux
      if (jobData.status === 'completed') {
        setGenerating(false);
        setScrapeAlert(null);
        
        // Détecter les sites bloqués par le filtre de contenu du LLM
        const llmBlocked = (jobData.sites_status || []).filter(s => s.error_type === 'llm_content_filter');
        if (llmBlocked.length > 0 && !completionNotifiedRef.current) {
          setLlmErrorAlert({
            sites: llmBlocked.map(s => s.site_name || s.site_id),
            message: llmBlocked[0].error || '',
          });
        }
        
        if (!completionNotifiedRef.current) {
          completionNotifiedRef.current = true;
          const linksTotal = jobData.cluster_total_links_injected || 0;
          if (jobData.completed_sites > 0) {
            if (linksTotal > 0) {
              toast.success(`${jobData.completed_sites} article(s) généré(s) — ${linksTotal} liens cluster injectés`);
            } else {
              toast.success(`${jobData.completed_sites} article(s) généré(s) !`);
            }
          }
        }
        setTotalCost(jobData.total_cost_eur);
        
        // Recharger les articles pour récupérer les liens cluster
        // Utiliser une mise à jour fonctionnelle pour éviter les race conditions
        const refreshArticles = async () => {
          try {
            const sitesToRefresh = (jobData.sites_status || []).filter(
              site => site.article_id && site.status === 'completed'
            );
            
            const refreshedArticles = await Promise.all(
              sitesToRefresh.map(async (site) => {
                try {
                  const r = await axios.get(`${API}/api/articles/${site.article_id}`);
                  return {
                    siteId: site.site_id,
                    data: { ...r.data, site_name: site.site_name, is_extra: site.is_extra || false }
                  };
                } catch (_) {
                  return null;
                }
              })
            );
            
            const validRefreshed = refreshedArticles.filter(a => a !== null);
            if (validRefreshed.length > 0) {
              setGeneratedArticles(prev => {
                const updated = { ...(prev || {}) };
                for (const { siteId, data } of validRefreshed) {
                  updated[siteId] = data;
                }
                return updated;
              });
            }
          } catch (e) { /* non critique */ }
        };
        refreshArticles();
        
      } else if (jobData.status === 'error') {
        setGenerating(false);
        setCurrentJobId(null);
        setScrapeAlert(null);
        const llmBlocked = (jobData.sites_status || []).filter(s => s.error_type === 'llm_content_filter');
        if (llmBlocked.length > 0) {
          setLlmErrorAlert({
            sites: llmBlocked.map(s => s.site_name || s.site_id),
            message: llmBlocked[0].error || '',
          });
        } else {
          toast.error('Erreur lors de la génération');
        }
        
      } else if (jobData.status === 'cancelled') {
        setGenerating(false);
        setCurrentJobId(null);
        setScrapeAlert(null);
        toast.info('Génération annulée');
        
      } else if (jobData.status === 'scrape_failed' && !scrapeAlert) {
        setScrapeAlert({ jobId: currentJobId });
      }
    });
    
    return () => unsubscribe();
  }, [currentJobId, subscribeToJob, scrapeAlert]);  // Retiré generatedArticles car on utilise la mise à jour fonctionnelle

  // Solution C: Refetch forcé quand l'onglet redevient visible
  // Garantit l'affichage même si le polling a raté une mise à jour
  useEffect(() => {
    if (!currentJobId) return;
    
    const handleVisibilityChange = async () => {
      if (!document.hidden && currentJobId) {
        try {
          const response = await axios.get(`${API}/api/jobs/${currentJobId}/status`);
          const jobData = response.data;
          
          // Mettre à jour l'état local
          setJobStatus(jobData);
          setLastPollTime(Date.now());
          
          // Si le job est terminé, charger les articles
          if (jobData.status === 'completed' && jobData.sites_status) {
            const articlesToLoad = jobData.sites_status
              .filter(site => site.article_id && site.status === 'completed')
              .map(site => ({
                siteId: site.site_id,
                articleId: site.article_id,
                siteName: site.site_name,
                isExtra: site.is_extra || false
              }));
            
            if (articlesToLoad.length > 0) {
              const loadedArticles = await Promise.all(
                articlesToLoad.map(async ({ siteId, articleId, siteName, isExtra }) => {
                  try {
                    const articleRes = await axios.get(`${API}/api/articles/${articleId}`);
                    return {
                      siteId,
                      data: { ...articleRes.data, site_name: siteName, is_extra: isExtra }
                    };
                  } catch (e) {
                    return null;
                  }
                })
              );
              
              const validArticles = loadedArticles.filter(a => a !== null);
              if (validArticles.length > 0) {
                setGeneratedArticles(prev => {
                  const updated = { ...(prev || {}) };
                  for (const { siteId, data } of validArticles) {
                    updated[siteId] = data;
                  }
                  return updated;
                });
                
                // Arrêter l'état "generating" si encore actif
                if (generating) {
                  setGenerating(false);
                }
              }
            }
          }
        } catch (e) {
          console.error('[CreateArticle] Visibility refetch error:', e);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentJobId, generating]);

  // Timer pour afficher le temps écoulé depuis le début de la génération
  useEffect(() => {
    if (!generating || !generationStartTime) {
      setElapsedTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - generationStartTime) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [generating, generationStartTime]);

  // ================================================================
  // WATCHDOG DE SÉCURITÉ - Filet de sécurité si le polling échoue
  // ================================================================
  // Si aucune mise à jour n'est reçue depuis 15 secondes pendant la génération,
  // effectue un fetch direct au backend pour récupérer le statut et les articles
  useEffect(() => {
    if (!generating || !currentJobId) return;
    
    const WATCHDOG_INTERVAL_MS = 5000;  // Vérifier toutes les 5 secondes
    const STALE_THRESHOLD_MS = 15000;   // Considérer "stale" après 15 secondes sans mise à jour
    
    const watchdogCheck = async () => {
      const now = Date.now();
      const timeSinceLastUpdate = lastPollTime ? (now - lastPollTime) : STALE_THRESHOLD_MS + 1;
      
      // Si on n'a pas reçu de mise à jour depuis trop longtemps
      if (timeSinceLastUpdate > STALE_THRESHOLD_MS) {
        console.log(`[Watchdog] Aucune mise à jour depuis ${Math.round(timeSinceLastUpdate / 1000)}s - fetch direct`);
        
        try {
          const response = await axios.get(`${API}/api/jobs/${currentJobId}/status`);
          const jobData = response.data;
          
          // Mettre à jour le timestamp
          setLastPollTime(Date.now());
          setJobStatus(jobData);
          
          // Si le job est terminé, charger les articles
          if (jobData.status === 'completed' || jobData.status === 'completed_with_errors') {
            console.log('[Watchdog] Job terminé détecté - chargement des articles');
            
            const articlesToLoad = (jobData.sites_status || [])
              .filter(site => site.article_id && site.status === 'completed')
              .map(site => ({
                siteId: site.site_id,
                articleId: site.article_id,
                siteName: site.site_name,
                isExtra: site.is_extra || false
              }));
            
            if (articlesToLoad.length > 0) {
              const loadedArticles = await Promise.all(
                articlesToLoad.map(async ({ siteId, articleId, siteName, isExtra }) => {
                  try {
                    const articleRes = await axios.get(`${API}/api/articles/${articleId}`);
                    return {
                      siteId,
                      data: { ...articleRes.data, site_name: siteName, is_extra: isExtra }
                    };
                  } catch (e) {
                    console.error('[Watchdog] Erreur chargement article:', e);
                    return null;
                  }
                })
              );
              
              const validArticles = loadedArticles.filter(a => a !== null);
              if (validArticles.length > 0) {
                setGeneratedArticles(prev => {
                  const updated = { ...(prev || {}) };
                  for (const { siteId, data } of validArticles) {
                    updated[siteId] = data;
                  }
                  return updated;
                });
              }
            }
            
            // Arrêter la génération
            setGenerating(false);
            setTotalCost(jobData.total_cost_eur);
            
            if (!completionNotifiedRef.current) {
              completionNotifiedRef.current = true;
              const linksTotal = jobData.cluster_total_links_injected || 0;
              if (jobData.completed_sites > 0) {
                if (linksTotal > 0) {
                  toast.success(`${jobData.completed_sites} article(s) généré(s) — ${linksTotal} liens cluster injectés`);
                } else {
                  toast.success(`${jobData.completed_sites} article(s) généré(s) !`);
                }
              }
            }
          } else if (jobData.status === 'error' || jobData.status === 'cancelled') {
            setGenerating(false);
            setCurrentJobId(null);
            if (jobData.status === 'error') {
              toast.error('Erreur lors de la génération');
            } else {
              toast.info('Génération annulée');
            }
          }
        } catch (e) {
          console.error('[Watchdog] Erreur fetch direct:', e);
        }
      }
    };
    
    const watchdogInterval = setInterval(watchdogCheck, WATCHDOG_INTERVAL_MS);
    
    return () => clearInterval(watchdogInterval);
  }, [generating, currentJobId, lastPollTime]);

  // formatElapsedTime est maintenant importé depuis ../components/CreateArticle

  // ================================================================
  // RÉGÉNÉRATION D'ARTICLES
  // ================================================================
  
  // Vérifier si un site a déjà une v2
  const hasV2 = (siteId) => {
    const articles = generatedArticles?.[siteId];
    if (!articles) return false;
    // Si c'est un objet avec v1/v2
    if (articles.v2) return true;
    // Si l'article a version: 2
    if (articles.version === 2) return true;
    return false;
  };

  // Lancer la régénération
  const handleRegenerate = async () => {
    const sitesToRegen = Object.entries(regenSites)
      .filter(([_, data]) => data.selected)
      .map(([siteId, data]) => ({
        site_id: siteId,
        angle: data.angle || '',
        title: data.title || null
      }));
    
    if (sitesToRegen.length === 0) {
      toast.error('Sélectionnez au moins un site à régénérer');
      return;
    }
    
    if (!currentJobId) {
      toast.error('Aucun job associé pour la régénération');
      return;
    }
    
    setRegenerating(true);
    
    try {
      const response = await axios.post(`${API}/api/jobs/${currentJobId}/regenerate`, {
        job_id: currentJobId,
        sites: sitesToRegen
      });
      
      const newJobId = response.data.job_id;
      setRegenJobId(newJobId);
      toast.success(`Régénération lancée pour ${sitesToRegen.length} site(s)...`);
      
      // Polling du job de régénération
      const pollRegen = async () => {
        try {
          const statusRes = await axios.get(`${API}/api/jobs/${newJobId}/status`);
          const status = statusRes.data;
          
          // Mettre à jour jobStatus pour afficher les logs verbose
          setJobStatus(prev => ({
            ...prev,
            ...status,
            verbose_log: status.verbose_log || prev?.verbose_log || []
          }));
          
          if (status.status === 'completed' || status.status === 'completed_with_errors') {
            // Récupérer les nouveaux articles v2
            for (const siteStatus of status.sites_status || []) {
              if (siteStatus.status === 'completed' && siteStatus.article_id) {
                try {
                  const articleRes = await axios.get(`${API}/api/articles/${siteStatus.article_id}`);
                  const v2Article = articleRes.data;
                  
                  // Ajouter comme v2 à côté de v1
                  setGeneratedArticles(prev => {
                    const existing = prev[siteStatus.site_id];
                    // Restructurer avec v1/v2
                    return {
                      ...prev,
                      [siteStatus.site_id]: {
                        v1: existing?.v1 || existing,  // Garder v1 existante
                        v2: {
                          ...v2Article,
                          regen_angle: regenSites[siteStatus.site_id]?.angle || ''
                        },
                        selectedVersion: 2  // Sélectionner v2 par défaut
                      }
                    };
                  });
                } catch (artErr) {
                  console.error('Error fetching regenerated article:', artErr);
                }
              }
            }
            
            setRegenerating(false);
            setRegenJobId(null);
            setShowRegenPanel(false);
            setRegenSites({});
            toast.success('Régénération terminée !');
            return true;
          }
          
          if (status.status === 'error') {
            setRegenerating(false);
            setRegenJobId(null);
            toast.error('Erreur lors de la régénération');
            return true;
          }
          
          return false;  // Continuer le polling
        } catch (err) {
          console.error('Error polling regen job:', err);
          return false;
        }
      };
      
      // Lancer le polling
      const regenInterval = setInterval(async () => {
        const done = await pollRegen();
        if (done) clearInterval(regenInterval);
      }, 2000);
      
    } catch (error) {
      console.error('Error starting regeneration:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la régénération');
      setRegenerating(false);
    }
  };

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API}/api/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des sites');
    }
  };

  const toggleSite = (siteId) => {
    setSelectedSites(prev =>
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleRecordingComplete = async (audioBlob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/api/voice-memos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Audio transcrit !');
      setUploadedAudio(response.data);
    } catch (error) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    const uploadedResults = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/api/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedResults.push({
          id: response.data.id,
          name: file.name,
          path: response.data.file_path
        });
      } catch (error) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    setAdditionalFiles(prev => [...prev, ...uploadedResults]);
    setUploadingFiles(false);
    
    if (uploadedResults.length > 0) {
      toast.success(`${uploadedResults.length} fichier(s) ajouté(s)`);
    }
  };

  const removeFile = (fileId) => {
    setAdditionalFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleGenerate = async () => {
    // Déterminer le contenu principal selon le mode
    let mainContent = '';
    if (inputMode === 'dictee') {
      mainContent = dicteeText;
    } else if (inputMode === 'texte') {
      mainContent = pastedText;
    } else if (inputMode === 'document' && uploadedDocument) {
      mainContent = uploadedDocument.content || '';
    }

    // En mode document, ajouter l'ID du document aux fichiers additionnels
    // pour que le backend récupère le texte complet (pas le preview tronqué)
    const allFileIds = [
      ...additionalFiles.map(f => f.id),
      ...(inputMode === 'document' && uploadedDocument?.id ? [uploadedDocument.id] : [])
    ];

    if (!mainContent && additionalFiles.length === 0 && !additionalUrls) {
      toast.error('Ajoutez du contenu (texte, document, URLs ou fichiers)');
      return;
    }

    if (selectedSites.length === 0 && extraSlots.length === 0) {
      toast.error('Sélectionnez au moins un site ou ajoutez un article supplémentaire');
      return;
    }

    // Validation SEO obligatoire (sauf si override activé)
    if (!seoOverride && !primarySeoKeyword.trim()) {
      toast.error('Le mot-clé SEO principal est obligatoire. Cochez "Ignorer l\'obligation SEO" pour passer outre.');
      return;
    }

    // Validation recherche internet obligatoire (sauf si override activé)
    if (!searchOverride && !autoEnrich) {
      toast.error('La recherche internet est obligatoire. Cochez "Ignorer l\'obligation recherche" pour passer outre.');
      return;
    }

    // Validation requête personnalisée obligatoire (sauf si override activé)
    if (!queryOverride && autoEnrich && !enrichQuery.trim()) {
      toast.error('La requête de recherche est obligatoire. Cochez "Ignorer l\'obligation requête" pour passer outre.');
      return;
    }

    // Validation titres utilisateur obligatoires (sauf si override activé)
    const selectedSiteIds = sites.filter(s => s.selected).map(s => s.id);
    const hasMissingTitles = selectedSiteIds.some(siteId => !siteTitles[siteId]?.trim());
    if (!titlesOverride && hasMissingTitles) {
      toast.error('Les titres sont obligatoires pour tous les sites. Cochez "Ignorer l\'obligation titres" pour passer outre.');
      return;
    }

    // Valider les extra slots : au moins un label et un prompt
    const validExtraSlots = extraSlots.filter(s => s.prompt.trim());
    if (extraSlots.length > 0 && validExtraSlots.length === 0) {
      toast.error('Remplissez le prompt des articles supplémentaires');
      return;
    }

    setGenerating(true);
    setGeneratedArticles(null);
    setPublishedArticles({});
    setJobStatus(null);
    setGenerationStartTime(Date.now()); // Démarrer le timer
    setLastPollTime(null);
    completionNotifiedRef.current = false; // Réinitialiser le flag de notification
    sessionStorage.removeItem(storageKey);
    
    try {
      // Utiliser l'ordre défini par l'utilisateur
      const orderedSiteIds = orderedSites.map(s => s.id);
      
      
      
      // Parser les mots-clés secondaires (séparés par virgule)
      const secondaryKwList = secondaryKeywords
        .split(',')
        .map(kw => kw.trim())
        .filter(kw => kw.length > 0);
      
      // Créer un job asynchrone
      const response = await axios.post(`${API}/api/jobs/create`, {
        transcription_override: mainContent || uploadedAudio?.transcription,
        additional_notes: additionalNotes,
        additional_urls: additionalUrls,
        additional_file_ids: allFileIds,
        site_ids: orderedSiteIds,
        site_prompts: sitePrompts,
        site_rewrite_prompts: siteRewritePrompts,
        generate_image: imageMode !== 'none',
        image_mode: imageMode,
        content_type: contentType,
        site_titles: siteTitles,
        user_title: Object.values(siteTitles).find(t => t) || null,
        primary_seo_keyword: primarySeoKeyword.trim() || null,
        secondary_keywords: secondaryKwList.length > 0 ? secondaryKwList : null,
        image_search_keyword: imageSearchKeyword.trim() || null,
        auto_enrich: autoEnrich,
        enrich_languages: autoEnrich ? (enrichLanguages.length ? enrichLanguages : ['fr']) : null,
        enrich_query: autoEnrich ? (enrichQuery.trim() || null) : null,
        extra_prompts: validExtraSlots.map(s => ({
          label: s.label.trim() || `Article supplémentaire`,
          prompt: s.prompt.trim(),
          title: s.title?.trim() || ''
        })),
        // Cluster SEO : maillage automatique en backend après génération
        cluster_mode: clusterMode && Object.values(clusterLinks).some(targets => targets.length > 0),
        cluster_links: (clusterMode ? Object.entries(clusterLinks) : []).flatMap(
          ([sourceSiteId, targetIds]) => (targetIds || []).map(targetId => ({
            from_site_id: sourceSiteId,
            to_site_id: targetId,
          }))
        ),
        // Réécriture anti-watermark GPT-5.5 (activée par défaut)
        enable_openai_rewrite: enableOpenaiRewrite,
      });

      
      toast.success('Génération lancée !');
      
      // Stocker le job ID pour le polling
      const jobId = response.data.job_id;
      setCurrentJobId(jobId);
      
      // Enregistrer le job dans le JobContext (démarre le polling centralisé)
      registerJob(jobId, user?.id, {
        total_sites: response.data.total_sites,
        sites: response.data.sites.map(s => ({
          ...s,
          status: 'pending',
          progress: 0
        }))
      });
      
      setJobStatus({
        status: 'pending',
        total_sites: response.data.total_sites,
        completed_sites: 0,
        sites_status: response.data.sites.map(s => ({
          ...s,
          status: 'pending',
          progress: 0
        }))
      });
      setTimeout(() => {
        document.getElementById('generated-articles')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      
    } catch (error) {
      console.error('Generation error:', error);
      console.error('Error response:', error.response?.data);
      
      // Afficher un message d'erreur plus détaillé
      let errorMsg = 'Erreur lors de la génération';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Délai dépassé - la génération prend trop de temps. Réessayez.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      setGenerating(false);
    }
  };

  // État pour tracker les articles publiés (déjà déclaré plus haut avec restauration sessionStorage)

  const handlePublish = async (siteId, articleData, status, forcePublish = false) => {
    // Vérification rapide : site connecté ?
    const site = sites.find(s => s.id === siteId);
    if (site && site.is_connected === false) {
      toast.error(`Le site "${site.name}" n'est pas connecté. Vérifiez les identifiants WordPress.`);
      return;
    }
    
    setPublishing(true);
    try {
      // L'ID peut être dans 'id' ou 'article_id' selon la source
      const articleId = articleData.id || articleData.article_id;
      
      if (!articleId) {
        toast.error('ID article manquant');
        setPublishing(false);
        return;
      }
      
      // Sauvegarder les modifications AVANT de publier
      // Cela garantit que les modifications de l'éditeur sont envoyées à WordPress
      try {
        await axios.put(`${API}/api/articles/${articleId}`, articleData);
      } catch (saveErr) {
        // Erreur de sauvegarde non bloquante
        // On continue quand même la publication
      }
      
      const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
        article_id: articleId,
        categories: [],
        tags: [],
        publish_status: status,
        custom_prompt: sitePrompts[siteId] || '',
        force_publish: forcePublish
      });
      
      // Gestion de l'avertissement SEO
      if (response.data?.warning) {
        const score = response.data.seo_score;
        // Important: garder publishing=true pendant que le toast est affiché
        // car l'utilisateur peut cliquer sur "Forcer"
        setPublishing(false);
        
        toast.custom(
          (toastId) => (
            <div className="bg-slate-900 border border-amber-500/50 rounded-lg p-4 shadow-lg max-w-sm">
              <p className="font-semibold text-amber-400">Score SEO faible ({score}/100)</p>
              <p className="text-sm mt-1 text-slate-300">Voulez-vous publier quand même ?</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { toast.dismiss(toastId); handlePublish(siteId, articleData, status, true); }}
                  className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-sm font-semibold">
                  Forcer la publication
                </button>
                <button onClick={() => toast.dismiss(toastId)}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm">
                  Annuler
                </button>
              </div>
            </div>
          ),
          { duration: 30000 }  // 30 secondes au lieu de 15 pour laisser le temps de décider
        );
        return;  // Ne pas aller dans le finally
      }
      
      const statusText = status === 'publish' ? 'publié' : 'enregistré en brouillon';
      
      // Get the post URL from response
      const postUrl = response.data?.url || response.data?.link;
      
      // Marquer cet article comme publié
      setPublishedArticles(prev => ({
        ...prev,
        [siteId]: {
          status: status,
          url: postUrl,
          post_id: response.data?.post_id
        }
      }));
      
      if (postUrl) {
        toast.success(
          <div>
            <p>Article {statusText} sur {sites.find(s => s.id === siteId)?.name || 'WordPress'} !</p>
            <a href={postUrl} target="_blank" rel="noopener noreferrer" className="underline text-sky-300 mt-1 block">
              Voir l&apos;article →
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.success(`Article ${statusText} !`);
      }
      
      // NE PAS rediriger automatiquement - laisser l'utilisateur publier les autres articles
      
    } catch (error) {
      console.error('Publish error:', error);
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error('Erreur lors de la publication');
      }
    } finally {
      setPublishing(false);
    }
  };

  const getFaviconUrl = (siteUrl) => {
    try {
      const url = new URL(siteUrl);
      return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
    } catch {
      return null;
    }
  };

  const addExtraSlot = () => {
    if (extraSlots.length >= 3) return;
    setExtraSlots(prev => [...prev, { id: `extra_${Date.now()}`, label: '', prompt: '', title: '' }]);
  };

  const removeExtraSlot = (slotId) => {
    setExtraSlots(prev => prev.filter(s => s.id !== slotId));
  };

  const updateExtraSlot = (slotId, field, value) => {
    setExtraSlots(prev => prev.map(s => s.id === slotId ? { ...s, [field]: value } : s));
  };

  // Publication multi-sites avec délai
  const handlePublishAll = async (status) => {
    const siteIds = Object.keys(generatedArticles || {});
    if (siteIds.length === 0) return;
    
    // Vérification rapide : filtrer les sites déconnectés
    const disconnectedSites = siteIds
      .map(id => sites.find(s => s.id === id))
      .filter(site => site && site.is_connected === false);
    
    if (disconnectedSites.length > 0) {
      const names = disconnectedSites.map(s => s.name).join(', ');
      toast.error(`Site(s) non connecté(s) : ${names}. Vérifiez les identifiants WordPress.`, { duration: 8000 });
      // Continuer avec les sites connectés seulement
    }
    
    setPublishing(true);
    
    // Sauvegarder TOUS les articles modifiés avant de publier
    for (const siteId of siteIds) {
      const articleData = generatedArticles[siteId];
      const articleId = articleData?.id || articleData?.article_id;
      if (articleId) {
        try {
          await axios.put(`${API}/api/articles/${articleId}`, articleData);
        } catch (saveErr) {
          // Erreur de sauvegarde non bloquante
        }
      }
    }
    
    for (let i = 0; i < siteIds.length; i++) {
      const siteId = siteIds[i];
      const articleData = generatedArticles[siteId];
      const site = sites.find(s => s.id === siteId);
      
      if (publishedArticles[siteId]) continue; // Déjà publié
      if (site && site.is_connected === false) continue; // Site déconnecté (déjà notifié)
      
      const siteName = site?.name || siteId;
      
      // Attendre le délai entre publications (sauf pour le premier)
      if (i > 0 && publishDelay > 0) {
        const delayMs = publishDelay * 60 * 1000;
        toast.info(`Publication sur ${siteName} dans ${publishDelay} min...`, { duration: 5000 });
        
        // Planifier la publication différée via WordPress (date future)
        const futureDate = new Date(Date.now() + delayMs * i);
        try {
          const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
            article_id: articleData.id || articleData.article_id,
            publish_status: status,
            scheduled_date: futureDate.toISOString(),
            custom_prompt: sitePrompts[siteId] || '',
            force_publish: true  // Forcer pour éviter les popups SEO en mode batch
          });
          
          const postUrl = response.data?.url || response.data?.link;
          setPublishedArticles(prev => ({
            ...prev,
            [siteId]: { status: status === 'publish' ? 'future' : 'draft', url: postUrl, post_id: response.data?.post_id }
          }));
          toast.success(`${siteName} : planifié à ${futureDate.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}`);
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(detail || `Erreur pour ${siteName}`, { duration: 8000 });
        }
      } else {
        // Publication immédiate (premier article ou pas de délai)
        try {
          const response = await axios.post(`${API}/api/wordpress-sites/${siteId}/publish`, {
            article_id: articleData.id || articleData.article_id,
            publish_status: status,
            custom_prompt: sitePrompts[siteId] || '',
            force_publish: true  // Forcer pour éviter les popups SEO en mode batch
          });
          
          const postUrl = response.data?.url || response.data?.link;
          setPublishedArticles(prev => ({
            ...prev,
            [siteId]: { status: status, url: postUrl, post_id: response.data?.post_id }
          }));
          
          const statusText = status === 'publish' ? 'publié' : 'brouillon';
          toast.success(`${siteName} : ${statusText}`, { duration: 3000 });
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(detail || `Erreur pour ${siteName}`, { duration: 8000 });
        }
      }
    }
    setPublishing(false);
  };

  // Mode site unique (venu du dashboard)
  const isSingleSiteMode = !!preselectedSite;

  // Upload document handler
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/api/documents/upload`, formData);
      setUploadedDocument({
        id: response.data.id,
        name: file.name,
        content: response.data.content
      });
      toast.success('Document uploadé');
    } catch (error) {
      toast.error('Erreur upload document');
    } finally {
      setUploading(false);
    }
  };

  // Upload d'image personnalisée pour un article spécifique
  const handleCustomImageUpload = async (e, siteId, articleData) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier que c'est une image
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image (JPG, PNG, WebP...)');
      return;
    }
    
    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('L\'image est trop volumineuse (max 10 Mo)');
      return;
    }
    
    setUploadingCustomImage(prev => ({ ...prev, [siteId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', siteId);
      formData.append('article_title', articleData.title || '');
      formData.append('article_slug', articleData.slug || '');
      
      const response = await axios.post(`${API}/api/articles/upload-custom-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Mettre à jour l'article avec l'URL de l'image uploadée
      setGeneratedArticles(prev => ({
        ...prev,
        [siteId]: { 
          ...prev[siteId], 
          featured_image_url: response.data.image_url,
          custom_image_uploaded: true,
          custom_image_filename: response.data.filename,
          no_image: false
        }
      }));
      
      toast.success('Image uploadée avec succès');
    } catch (error) {
      console.error('Custom image upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingCustomImage(prev => ({ ...prev, [siteId]: false }));
    }
  };

  // Fonction helper pour mettre à jour les données d'un article
  const updateArticleData = useCallback((siteId, updates, isRootLevel = false) => {
    setGeneratedArticles(prev => {
      if (!prev || !prev[siteId]) return prev;
      
      const rawArticleData = prev[siteId];
      
      // Si mise à jour au niveau racine (ex: selectedVersion)
      if (isRootLevel) {
        return { ...prev, [siteId]: { ...rawArticleData, ...updates } };
      }
      
      // Mettre à jour l'article (pipeline V2 uniquement)
      if (rawArticleData?.v2) {
        return { ...prev, [siteId]: { ...rawArticleData, v2: { ...rawArticleData.v2, ...updates } } };
      } else {
        return { ...prev, [siteId]: { ...rawArticleData, ...updates } };
      }
    });
  }, []);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="create-article">
      {/* Pop-up : génération bloquée par le filtre de contenu du LLM (pas une erreur Auto-Edito) */}
      <Dialog open={!!llmErrorAlert} onOpenChange={(open) => { if (!open) setLlmErrorAlert(null); }}>
        <DialogContent className="bg-slate-900 border border-amber-500/40 text-slate-100 max-w-lg" data-testid="llm-error-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Génération bloquée par le modèle IA
            </DialogTitle>
            <DialogDescription className="text-slate-300 pt-2 space-y-3">
              <span className="block">
                Cette interruption ne vient <strong className="text-slate-100">pas d'Auto-Edito</strong>. Elle est provoquée par le
                <strong className="text-amber-300"> filtre de contenu du fournisseur d'IA (LLM)</strong>, qui a refusé de rédiger
                ce texte car le sujet a été jugé sensible (santé publique, épidémies, armement, etc.).
              </span>
              {llmErrorAlert?.sites?.length > 0 && (
                <span className="block text-sm">
                  Site(s) concerné(s) : <strong className="text-slate-100">{llmErrorAlert.sites.join(', ')}</strong>
                </span>
              )}
              <span className="block text-sm text-slate-400">
                Conseil : reformulez le sujet ou le titre avec un angle plus neutre et factuel, puis relancez la génération.
                Le système bascule déjà automatiquement vers un modèle de secours, mais certains sujets restent bloqués par le filtre.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setLlmErrorAlert(null)}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="llm-error-dialog-close"
            >
              J'ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl md:text-4xl font-bold font-mono tracking-widest uppercase text-sky-400 mb-2"
                style={{ textShadow: '0 0 30px rgba(0,229,255,0.3)' }}>
              {isSingleSiteMode ? `MISSION_${preselectedSite.name.toUpperCase().replace(/\s/g, '_')}` : 'NOUVELLE_MISSION'}
            </h1>
            <ServiceLLMToggle serviceId="create" />
          </div>
          {isSingleSiteMode && (
            <div className="flex items-center gap-3 mt-3">
              <img 
                src={getFaviconUrl(preselectedSite.url)} 
                alt="" 
                className="w-8 h-8 rounded"
              />
              <span className="text-slate-500 font-mono text-sm">{preselectedSite.url}</span>
            </div>
          )}
        </div>

        {/* Si pas d'articles générés, afficher le formulaire */}
        {!generatedArticles && (
          <div className="space-y-6">

            {/* Bouton Liste des cibles */}
            <button
              onClick={() => navigate('/cibles')}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 hover:border-sky-500/50 rounded-lg transition-all text-sky-400 hover:text-sky-300 font-mono"
              data-testid="back-to-targets-btn"
            >
              <Target className="w-5 h-5" />
              <span className="text-sm uppercase tracking-wider">Liste des cibles</span>
            </button>

            {/* Type de contenu - placé juste après Mode d'entrée */}
            {/* Zone de saisie selon le mode */}
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-mono uppercase text-sky-400 flex items-center">
                  <Mic className="w-6 h-6 mr-3" /> VOTRE_SUJET
                </CardTitle>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowHistory(!showHistory); setShowPrompts(false); }}
                    className={`p-2 transition-colors ${showHistory ? 'text-sky-400' : 'text-slate-400 hover:text-sky-400'}`}
                    title="3 dernières créations (paramètres)"
                    data-testid="creation-history-btn"
                  >
                    <History className="w-5 h-5" />
                  </button>
                  <button
                    onClick={restoreLastJob}
                    disabled={!lastJobSnapshot}
                    className={`p-2 transition-colors ${lastJobSnapshot ? 'text-slate-400 hover:text-emerald-400' : 'text-slate-700 cursor-not-allowed opacity-40'}`}
                    title={
                      lastJobSnapshot
                        ? `Reprendre le dernier job généré (${new Date(lastJobSnapshot.saved_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})`
                        : "Aucun job enregistré"
                    }
                    data-testid="last-job-restore-btn"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => { setShowPrompts(!showPrompts); setShowHistory(false); }}
                    className="p-2 text-slate-400 hover:text-sky-400 transition-colors"
                    title="Prompts sauvegardés"
                  >
                    <Save className="w-5 h-5" />
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Historique des 3 dernières créations */}
                {showHistory && (
                  <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-sky-500/30 max-h-64 overflow-y-auto" data-testid="creation-history-panel">
                    <p className="text-xs text-sky-400 uppercase mb-2 font-semibold">3 dernières créations</p>
                    {creationHistory.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">Aucun historique</p>
                    ) : (
                      creationHistory.map((entry) => (
                        <div
                          key={entry.id}
                          onClick={() => loadFromHistory(entry)}
                          className="p-2.5 mb-1.5 hover:bg-sky-500/10 rounded-lg cursor-pointer border border-slate-800 hover:border-sky-500/30 transition-all group"
                          data-testid={`history-entry-${entry.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-sky-300 font-semibold truncate">{entry.label}</span>
                            <span className="text-[10px] text-slate-600 font-mono flex-shrink-0 ml-2">{new Date(entry.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 text-[10px]">
                            {entry.params?.image_mode && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{entry.params.image_mode}</span>
                            )}
                            {entry.params?.primary_seo_keyword && (
                              <span className="px-1.5 py-0.5 rounded bg-sky-900/30 text-sky-500">SEO: {entry.params.primary_seo_keyword}</span>
                            )}
                            {Object.keys(entry.params?.site_names || {}).length > 0 && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                                {Object.values(entry.params.site_names).slice(0, 3).join(', ')}
                              </span>
                            )}
                          </div>
                          {entry.params?.transcription && (
                            <p className="text-[10px] text-slate-600 mt-1 truncate">{entry.params.transcription}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Prompts sauvegardés */}
                {showPrompts && savedPrompts.length > 0 && (
                  <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700 max-h-48 overflow-y-auto">
                    <p className="text-xs text-slate-500 uppercase mb-2 font-semibold">Prompts sauvegardés</p>
                    {savedPrompts.map((prompt) => (
                      <div
                        key={prompt.id}
                        onClick={() => { setDicteeText(prompt.content); setShowPrompts(false); toast.success('Prompt chargé'); }}
                        className="flex items-center justify-between p-2 hover:bg-slate-800 rounded cursor-pointer group"
                      >
                        <span className="text-sm text-slate-300 truncate flex-1">{prompt.title}</span>
                        <button
                          onClick={(e) => deletePrompt(prompt.id, e)}
                          className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Zone de saisie / dictée */}
                <p className="text-slate-500 text-sm mb-3 font-mono">
                  Dictez ou tapez votre sujet d&apos;article
                </p>
                <Textarea
                  value={dicteeText}
                  onChange={(e) => setDicteeText(e.target.value)}
                  placeholder="Ex: L'inflation en France baisse à 1.8%..."
                  className="min-h-[120px] bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600 focus:border-sky-500/50"
                />
                
                {/* SEO - Mots-clés (obligatoires sauf override) - placés sous votre sujet */}
                <div className="mt-6 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-mono uppercase text-sky-400/80 flex items-center gap-2">
                      SEO_KEYWORDS
                      {!seoOverride && <span className="text-red-400 text-xs">*obligatoire</span>}
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={seoOverride}
                        onChange={(e) => setSeoOverride(e.target.checked)}
                        className="rounded border-slate-600 bg-slate-800 text-sky-500 focus:ring-sky-500/50"
                      />
                      <span>Ignorer l&apos;obligation SEO</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-mono mb-2 block">
                        Mot-clé SEO principal {!seoOverride && <span className="text-red-400">*</span>}
                      </label>
                      <Input
                        value={primarySeoKeyword}
                        onChange={(e) => setPrimarySeoKeyword(e.target.value)}
                        placeholder="ex: réforme des retraites 2026"
                        className={`bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600 ${
                          !seoOverride && !primarySeoKeyword.trim() ? 'border-red-500/50' : ''
                        }`}
                        data-testid="primary-seo-keyword"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Focus keyword pour le score SEO Yoast
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-mono mb-2 block">
                        Mots-clés secondaires
                      </label>
                      <Input
                        value={secondaryKeywords}
                        onChange={(e) => setSecondaryKeywords(e.target.value)}
                        placeholder="ex: pension, cotisation, âge départ"
                        className="bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600"
                        data-testid="secondary-keywords"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Séparés par virgule
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Compléments (angle, sources) - Sans les champs SEO qui sont maintenant au-dessus */}
            <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
              <CardHeader>
                <CardTitle className="text-lg font-mono uppercase text-sky-400 flex items-center">
                  <Paperclip className="w-5 h-5 mr-2" />
                  COMPLÉMENTS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* URLs */}
                <div>
                  <label className="text-xs text-slate-500 uppercase font-mono mb-2 flex items-center">
                    <Link className="w-4 h-4 mr-2" />
                    Sources supplémentaires (URLs)
                  </label>
                  <Textarea
                    value={additionalUrls}
                    onChange={(e) => setAdditionalUrls(e.target.value)}
                    placeholder="https://source1.com&#10;https://source2.com"
                    className="min-h-[60px] bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600 font-mono text-sm"
                  />
                </div>

                {/* Toggle enrichissement automatique - OBLIGATOIRE */}
                <div className="p-3 bg-slate-900/40 border border-slate-700 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-slate-300 font-semibold">Recherche d&apos;actualités</p>
                        {!searchOverride && <span className="text-red-400 text-xs">*obligatoire</span>}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">Recherche et scrape automatiquement des articles récents (&lt; 7 jours) pour enrichir le contexte</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-400">
                        <input
                          type="checkbox"
                          checked={searchOverride}
                          onChange={(e) => setSearchOverride(e.target.checked)}
                          className="w-3 h-3 rounded border-slate-600 bg-slate-800 text-sky-500"
                          data-testid="search-override-checkbox"
                        />
                        <span>Ignorer l&apos;obligation</span>
                      </label>
                      <button
                        onClick={() => setAutoEnrich(!autoEnrich)}
                        disabled={!searchOverride && autoEnrich}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${autoEnrich ? 'bg-emerald-500' : 'bg-slate-600'} ${!searchOverride && autoEnrich ? 'opacity-75 cursor-not-allowed' : ''}`}
                        data-testid="auto-enrich-toggle"
                        title={!searchOverride && autoEnrich ? 'Cochez "Ignorer l\'obligation" pour désactiver' : ''}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${autoEnrich ? 'translate-x-5.5 left-[22px]' : 'left-0.5'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Requête personnalisée - OBLIGATOIRE */}
                  {autoEnrich && (
                    <div className="pt-2 border-t border-slate-800">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[10px] text-slate-500 uppercase font-mono flex items-center gap-2">
                          Requête de recherche {!queryOverride && <span className="text-red-400">*obligatoire</span>}
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-400">
                          <input
                            type="checkbox"
                            checked={queryOverride}
                            onChange={(e) => setQueryOverride(e.target.checked)}
                            className="w-3 h-3 rounded border-slate-600 bg-slate-800 text-sky-500"
                            data-testid="query-override-checkbox"
                          />
                          <span>Ignorer l&apos;obligation</span>
                        </label>
                      </div>
                      <Input
                        value={enrichQuery}
                        onChange={(e) => setEnrichQuery(e.target.value)}
                        placeholder="Ex: Bouygues Telecom hausse prix forfait 2026"
                        className={`bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600 text-sm ${!queryOverride && !enrichQuery.trim() ? 'border-red-500/50' : ''}`}
                        data-testid="enrich-query-input"
                      />
                      <p className="text-[9px] text-slate-600 mt-1">Définit précisément ce que le système doit rechercher sur internet</p>
                    </div>
                  )}

                  {/* Sélection des langues ciblées */}
                  {autoEnrich && (
                    <div className="pt-2 border-t border-slate-800">
                      <p className="text-[10px] text-slate-500 uppercase font-mono mb-2">Langues ciblées</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { code: 'fr', flag: '🇫🇷', label: 'Français' },
                          { code: 'en', flag: '🇬🇧', label: 'Anglais' },
                          { code: 'it', flag: '🇮🇹', label: 'Italien' },
                          { code: 'de', flag: '🇩🇪', label: 'Allemand' },
                          { code: 'es', flag: '🇪🇸', label: 'Espagnol' },
                        ].map(({ code, flag, label }) => {
                          const active = enrichLanguages.includes(code);
                          return (
                            <button
                              key={code}
                              type="button"
                              data-testid={`enrich-lang-${code}`}
                              onClick={() => {
                                setEnrichLanguages((prev) => {
                                  if (prev.includes(code)) {
                                    const next = prev.filter((l) => l !== code);
                                    return next.length ? next : ['fr'];  // Ne jamais vider: fallback fr
                                  }
                                  return [...prev, code];
                                });
                              }}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-all ${
                                active
                                  ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300'
                                  : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-600'
                              }`}
                            >
                              <span className="text-sm leading-none">{flag}</span>
                              <span>{label}</span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1.5">
                        {enrichLanguages.length === 1 ? '1 langue sélectionnée' : `${enrichLanguages.length} langues sélectionnées`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes complémentaires */}
                <div>
                  <label className="text-xs text-slate-500 uppercase font-mono mb-2 block">
                    Notes / Angle éditorial
                  </label>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Angle à privilégier, points à mentionner..."
                    className="min-h-[60px] bg-slate-950/50 border-slate-700 text-slate-100 placeholder:text-slate-600"
                  />
                </div>

                {/* Documents complémentaires */}
                <div>
                  <label className="text-xs text-slate-500 uppercase font-mono mb-2 flex items-center">
                    <Upload className="w-4 h-4 mr-2" />
                    Documents complémentaires
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {additionalFiles.map((file) => (
                      <div key={file.id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/70 rounded-lg text-sm">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{file.name}</span>
                        <button onClick={() => removeFile(file.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center justify-center w-full h-16 border border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-slate-600 transition-colors">
                    {uploadingFiles ? (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-slate-600 mr-2" />
                        <span className="text-slate-500 text-sm">Ajouter des fichiers</span>
                      </>
                    )}
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploadingFiles}
                    />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Option Réécriture anti-watermark - Étape finale */}
            <Card className={`bg-slate-900/50 border-slate-800 ${enableOpenaiRewrite ? 'border-purple-500/30' : ''}`} data-testid="openai-rewrite-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-purple-400 font-mono uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Réécriture anti-watermark
                  </CardTitle>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className={`text-xs ${enableOpenaiRewrite ? 'text-purple-400' : 'text-slate-500'}`}>
                      {enableOpenaiRewrite ? 'Activée' : 'Désactivée'}
                    </span>
                    <div 
                      className={`relative w-10 h-5 rounded-full transition-colors ${enableOpenaiRewrite ? 'bg-purple-500' : 'bg-slate-600'}`}
                      onClick={() => setEnableOpenaiRewrite(!enableOpenaiRewrite)}
                      data-testid="openai-rewrite-toggle"
                      role="switch"
                      aria-checked={enableOpenaiRewrite}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enableOpenaiRewrite ? 'left-5' : 'left-0.5'}`} />
                    </div>
                  </label>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <p className="text-xs text-slate-500">
                  {enableOpenaiRewrite 
                    ? '✓ Les articles seront réécrits pour éliminer les traces de génération IA' 
                    : 'Ajoute une étape de réécriture pour un style plus naturel'}
                </p>
              </CardContent>
            </Card>

            {/* Site Selection - Composant extrait */}
            {!isSingleSiteMode && (
              <>
                {/* Options des titres utilisateur */}
                <div className="p-3 bg-slate-900/40 border border-slate-700 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-300 font-semibold flex items-center gap-2">
                        Titres imposés par l&apos;utilisateur
                        {!titlesOverride && <span className="text-red-400 text-xs">*obligatoire</span>}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Saisissez un titre personnalisé pour chaque site sélectionné</p>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-400">
                      <input
                        type="checkbox"
                        checked={titlesOverride}
                        onChange={(e) => setTitlesOverride(e.target.checked)}
                        className="w-3 h-3 rounded border-slate-600 bg-slate-800 text-amber-500"
                        data-testid="titles-override-checkbox"
                      />
                      <span>Ignorer l&apos;obligation</span>
                    </label>
                  </div>
                  
                  {/* Auto-suggestion IA des titres */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoSuggestTitles}
                          onChange={(e) => setAutoSuggestTitles(e.target.checked)}
                          className="w-3 h-3 rounded border-slate-600 bg-slate-800 text-violet-500"
                          data-testid="auto-suggest-titles-checkbox"
                        />
                        <span className="text-xs text-violet-400 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          Auto-suggestion IA
                        </span>
                      </label>
                    </div>
                    {autoSuggestTitles && orderedSites.length > 0 && (
                      <button
                        type="button"
                        onClick={suggestTitlesForSites}
                        disabled={suggestingTitles || (!enrichQuery.trim() && !additionalNotes.trim())}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded transition-colors"
                        data-testid="suggest-titles-btn"
                      >
                        {suggestingTitles ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Suggestion...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            Suggérer les titres
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <SiteSelector
                  sites={sites}
                  selectedSites={selectedSites}
                  orderedSites={orderedSites}
                  siteTitles={siteTitles}
                  sitePrompts={sitePrompts}
                  siteRewritePrompts={siteRewritePrompts}
                  showAdvanced={showAdvanced}
                  titlesRequired={!titlesOverride}
                  onToggleSite={toggleSite}
                  onUpdateSiteTitle={(siteId, value) => setSiteTitles(prev => ({ ...prev, [siteId]: value }))}
                  onUpdateSitePrompt={(siteId, value) => setSitePrompts(prev => ({ ...prev, [siteId]: value }))}
                  onUpdateSiteRewritePrompt={(siteId, value) => setSiteRewritePrompts(prev => ({ ...prev, [siteId]: value }))}
                  onToggleAdvanced={(siteId) => setShowAdvanced(prev => ({ ...prev, [siteId]: !prev[siteId] }))}
                  onMoveSiteUp={moveSiteUp}
                  onMoveSiteDown={moveSiteDown}
                />
              </>
            )}

            {/* Mode Cluster SEO - Maillage interne */}
            {orderedSites.length >= 2 && (
              <Card className={`bg-slate-900/50 border-slate-800 ${clusterMode ? 'border-emerald-500/30' : ''}`} data-testid="cluster-seo-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm text-emerald-400 font-mono uppercase tracking-wider flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      Cluster SEO
                    </CardTitle>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-slate-500">
                        {clusterMode ? 'Activé' : 'Désactivé'}
                      </span>
                      <div 
                        className={`relative w-10 h-5 rounded-full transition-colors ${clusterMode ? 'bg-emerald-500' : 'bg-slate-700'}`}
                        onClick={handleClusterToggle}
                        data-testid="cluster-toggle"
                        role="switch"
                        aria-checked={clusterMode}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${clusterMode ? 'left-5' : 'left-0.5'}`} />
                      </div>
                    </label>
                  </div>
                </CardHeader>
                {clusterMode && (
                  <CardContent className="space-y-4">
                    <p className="text-xs text-slate-500">
                      Configurez les liens entre vos articles. Seuls les sites <span className="text-emerald-400">configurés</span> (connectés + paramétrés) peuvent être liés.
                    </p>
                    
                    {/* Liste des sites éligibles avec multi-select */}
                    <div className="space-y-3">
                      <label className="text-xs text-slate-400 uppercase font-mono block">
                        Configuration des liens ({eligibleClusterSites.length} site{eligibleClusterSites.length > 1 ? 's' : ''} éligible{eligibleClusterSites.length > 1 ? 's' : ''})
                      </label>
                      
                      {eligibleClusterSites.length < 2 ? (
                        <div className="p-3 bg-sky-900/20 border border-sky-700/30 rounded text-xs text-sky-300">
                          <AlertTriangle className="w-4 h-4 inline mr-2" />
                          Au moins 2 sites configurés sont nécessaires pour le maillage.
                          Les sites doivent être connectés ET avoir un pitch ou prompt défini.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {orderedSites.map(site => {
                            const isEligible = isClusterEligible(site);
                            const currentLinks = clusterLinks[site.id] || [];
                            const otherEligibleSites = eligibleClusterSites.filter(s => s.id !== site.id);
                            
                            if (!isEligible) {
                              // Site non éligible - affiché en grisé
                              const isConnected = site.is_connected !== false;
                              const reason = !isConnected ? 'Déconnecté' : 'Non paramétré';
                              return (
                                <div key={site.id} className="flex items-center gap-3 p-2 rounded bg-slate-800/30 border border-slate-700/50 opacity-50">
                                  <span className={`w-2 h-2 rounded-full ${!isConnected ? 'bg-orange-500' : 'bg-red-500'}`} />
                                  <span className="text-sm text-slate-500 flex-1">{site.name}</span>
                                  <span className="text-xs text-slate-600 italic">{reason}</span>
                                </div>
                              );
                            }
                            
                            return (
                              <div key={site.id} className="p-3 rounded bg-slate-800/50 border border-emerald-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                  <span className="text-sm text-slate-200 font-medium">{site.name}</span>
                                  <span className="text-xs text-slate-500">lie vers →</span>
                                </div>
                                
                                {/* Multi-select des cibles */}
                                <div className="flex flex-wrap gap-2">
                                  {otherEligibleSites.map(targetSite => {
                                    const isLinked = currentLinks.includes(targetSite.id);
                                    return (
                                      <button
                                        key={targetSite.id}
                                        onClick={() => {
                                          if (isLinked) {
                                            updateClusterLinks(site.id, currentLinks.filter(id => id !== targetSite.id));
                                          } else {
                                            updateClusterLinks(site.id, [...currentLinks, targetSite.id]);
                                          }
                                        }}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-md border-2 transition-all ${
                                          isLinked
                                            ? 'bg-emerald-500 border-emerald-400 text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-600'
                                            : 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-white hover:border-emerald-400 hover:text-emerald-700'
                                        }`}
                                        data-testid={`cluster-target-${site.id}-${targetSite.id}`}
                                      >
                                        {isLinked && <Check className="w-3 h-3 inline mr-1" />}
                                        {targetSite.name}
                                      </button>
                                    );
                                  })}
                                  {otherEligibleSites.length === 0 && (
                                    <span className="text-xs text-slate-500 italic">Aucun autre site éligible</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Compteur de liens */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                        <span className="text-xs text-slate-500" data-testid="cluster-link-counter">
                          {totalClusterLinks} lien{totalClusterLinks > 1 ? 's' : ''} configuré{totalClusterLinks > 1 ? 's' : ''}
                        </span>
                        {totalClusterLinks === 0 && eligibleClusterSites.length >= 2 && (
                          <span className="text-xs text-sky-400">
                            Sélectionnez au moins un lien
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Modal d'alerte activation Cluster */}
            {showClusterWarning && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="cluster-warning-modal">
                <div className="bg-slate-900 border border-sky-500/50 rounded-lg p-6 max-w-md mx-4 shadow-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-sky-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Mode Cluster SEO
                      </h3>
                      <p className="text-sm text-slate-300 mb-4">
                        En activant ce mode, les articles générés contiendront des liens internes vers les autres articles du cluster.
                      </p>
                      <div className="p-3 bg-red-900/30 border border-red-500/40 rounded mb-4">
                        <p className="text-sm text-red-300 font-medium">
                          ⚠️ Important : Ne modifiez PAS les URLs (slugs) dans WordPress
                        </p>
                        <p className="text-xs text-red-400/80 mt-1">
                          Les liens entre articles sont générés avec les URLs prédites. Si vous modifiez un slug dans WordPress, les liens internes seront cassés (erreurs 404).
                        </p>
                      </div>
                      <div className="flex gap-3 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setShowClusterWarning(false)}
                          className="border-slate-600 text-slate-300"
                          data-testid="cluster-cancel-btn"
                        >
                          Annuler
                        </Button>
                        <Button
                          onClick={confirmClusterActivation}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="cluster-confirm-btn"
                        >
                          J&apos;ai compris, activer
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Progression du job */}
            {/* Barre de progression détaillée - s'affiche au début, avant que les articles n'apparaissent */}
            {jobStatus && generating && (!generatedArticles || Object.keys(generatedArticles).length === 0) && (
              <Card className="bg-slate-900/50 border-sky-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-sky-400 font-mono uppercase tracking-wider flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {jobStatus.pipeline_step === 'compendium' ? 'Analyse des sources...' :
                       jobStatus.pipeline_step === 'angles' ? 'Définition des angles...' :
                       `Génération en cours (${jobStatus.completed_sites}/${jobStatus.total_sites})`}
                    </div>
                    {/* Timer et indicateur de polling */}
                    <div className="flex items-center gap-3">
                      {/* Temps écoulé */}
                      <div className="flex items-center gap-1.5 text-xs">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-slate-400 tabular-nums">{formatElapsedTime(elapsedTime)}</span>
                      </div>
                      {/* Indicateur de "heartbeat" - clignote pour montrer que le polling fonctionne */}
                      <div 
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          lastPollTime && (Date.now() - lastPollTime) < 3000 
                            ? 'bg-emerald-500 animate-pulse' 
                            : 'bg-sky-500'
                        }`}
                        title={lastPollTime ? `Dernière MAJ: il y a ${Math.floor((Date.now() - lastPollTime) / 1000)}s` : 'En attente...'}
                      />
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Pipeline V2 : Étapes préparatoires */}
                  {jobStatus.pipeline_v2 && (
                    <div className="mb-4 pb-4 border-b border-slate-800">
                      <p className="text-xs text-slate-500 uppercase mb-3 font-mono">Pipeline V2 — Préparation</p>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Compendium */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                          jobStatus.pipeline_step === 'compendium' ? 'bg-purple-500/20 ring-1 ring-purple-500/50' :
                          jobStatus.compendium_stats ? 'bg-green-500/10' : 'bg-slate-800/50'
                        }`}>
                          {jobStatus.pipeline_step === 'compendium' ? (
                            <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                          ) : jobStatus.compendium_stats ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-500" />
                          )}
                          <div>
                            <p className={`text-xs font-medium ${
                              jobStatus.pipeline_step === 'compendium' ? 'text-purple-300' :
                              jobStatus.compendium_stats ? 'text-green-400' : 'text-slate-500'
                            }`}>
                              📚 Compendium
                            </p>
                            {jobStatus.compendium_stats && (
                              <p className="text-[10px] text-slate-500">
                                {jobStatus.compendium_stats.facts} faits, {jobStatus.compendium_stats.figures} chiffres
                              </p>
                            )}
                            {jobStatus.pipeline_step === 'compendium' && (
                              <p className="text-[10px] text-purple-400">Analyse en cours...</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Angles */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                          jobStatus.pipeline_step === 'angles' ? 'bg-sky-500/20 ring-1 ring-sky-500/50' :
                          jobStatus.angles_count ? 'bg-green-500/10' : 'bg-slate-800/50'
                        }`}>
                          {jobStatus.pipeline_step === 'angles' ? (
                            <Loader2 className="w-4 h-4 text-sky-400 animate-spin" />
                          ) : jobStatus.angles_count ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-500" />
                          )}
                          <div>
                            <p className={`text-xs font-medium ${
                              jobStatus.pipeline_step === 'angles' ? 'text-sky-300' :
                              jobStatus.angles_count ? 'text-green-400' : 'text-slate-500'
                            }`}>
                              🎯 Angles uniques
                            </p>
                            {jobStatus.angles_count && (
                              <p className="text-[10px] text-slate-500">
                                {jobStatus.angles_count} angles définis
                              </p>
                            )}
                            {jobStatus.pipeline_step === 'angles' && (
                              <p className="text-[10px] text-sky-400">Différenciation...</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Liste des sites */}
                  <div className="space-y-3">
                    {jobStatus.sites_status?.map((site, index) => {
                      const isActive = site.status !== 'completed' && site.status !== 'error' && site.status !== 'pending';
                      return (
                      <div key={site.site_id} className={`flex items-center gap-3 transition-all duration-300 ${isActive ? 'scale-[1.01]' : ''}`}>
                        <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold rounded transition-all ${
                          site.is_extra ? 'bg-amber-500/20 text-amber-400' : 
                          isActive ? 'bg-sky-500/20 text-sky-400 ring-2 ring-sky-500/30' : 
                          site.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-slate-800'
                        }`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm transition-colors ${isActive ? 'text-sky-300 font-medium' : 'text-slate-300'}`}>
                              {site.site_name}
                              {site.is_extra && <span className="ml-2 text-xs text-amber-400/60 font-mono">EXTRA</span>}
                            </span>
                            <span className={`text-xs font-mono flex items-center gap-1.5 ${
                              site.status === 'completed' ? 'text-green-400' :
                              site.status === 'error' ? 'text-red-400' :
                              site.status === 'pending' ? 'text-slate-500' :
                              site.status === 'analyzing' ? 'text-purple-400' :
                              site.status === 'defining_angle' ? 'text-amber-400' :
                              site.status === 'ready_to_generate' ? 'text-emerald-400' :
                              'text-sky-400'
                            }`}>
                              {isActive && <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />}
                              {site.status === 'completed' ? '✓ Terminé' :
                               site.status === 'error' ? '✗ Erreur' :
                               site.status === 'pending' ? '○ En attente' :
                               site.status === 'analyzing' ? '📚 Analyse...' :
                               site.status === 'defining_angle' ? '🎯 Angle...' :
                               site.status === 'ready_to_generate' ? '✓ Prêt' :
                               site.status === 'generating_text' ? '✍️ Rédaction...' :
                               site.status === 'generating_image' ? '🖼️ Image...' :
                               site.status === 'enriching' ? 'Recherche sources...' :
                               site.status === 'scraping' ? 'Analyse URLs...' :
                               site.status === 'improving_style' ? '✨ Réécriture...' :
                               site.status}
                            </span>
                          </div>
                          <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                            <div 
                              className={`h-1.5 rounded-full transition-all duration-500 ${
                                site.status === 'completed' ? 'bg-green-500' :
                                site.status === 'error' ? 'bg-red-500' :
                                isActive ? 'bg-gradient-to-r from-sky-500 to-sky-400 animate-pulse' :
                                'bg-sky-500'
                              }`}
                              style={{ width: `${site.progress || (isActive ? 50 : 0)}%` }}
                            />
                          </div>
                          {site.title && (
                            <p className="text-xs text-slate-500 mt-1 truncate">{site.title}</p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  {/* Message dynamique pour rassurer */}
                  <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-mono">
                      {(() => {
                        // Pipeline V2 : étapes préparatoires
                        if (jobStatus.pipeline_step === 'compendium') {
                          return '📚 Analyse et structuration des sources en cours...';
                        }
                        if (jobStatus.pipeline_step === 'angles') {
                          return '🎯 Définition des angles éditoriaux uniques...';
                        }
                        
                        const processing = jobStatus.sites_status?.find(s => s.status !== 'completed' && s.status !== 'error' && s.status !== 'pending');
                        if (processing) {
                          if (processing.status === 'generating_text') return `Rédaction de l'article pour ${processing.site_name}...`;
                          if (processing.status === 'generating_image') return `Génération de l'image pour ${processing.site_name}...`;
                          if (processing.status === 'enriching') return `Recherche de sources récentes...`;
                          if (processing.status === 'scraping') return `Analyse des URLs sources...`;
                          return `Traitement de ${processing.site_name}...`;
                        }
                        return 'Préparation du prochain article...';
                      })()}
                    </p>
                    {elapsedTime > 30 && (
                      <p className="text-[10px] text-slate-600">
                        {jobStatus.pipeline_v2 ? 'Pipeline V2 : articles différenciés' : 'Les articles longs peuvent prendre 1-2 min chacun'}
                      </p>
                    )}
                  </div>
                  {/* Verbose Log */}
                  {jobStatus.verbose_log && jobStatus.verbose_log.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-800">
                      <p className="text-xs text-slate-600 uppercase mb-2 font-mono">Journal détaillé</p>
                      <div className="max-h-40 overflow-y-auto space-y-0.5 font-mono text-xs" data-testid="verbose-log">
                        {jobStatus.verbose_log.map((log, i) => (
                          <p key={i} className={`${
                            log.includes('ERREUR') || log.includes('ERROR') || log.includes('❌') ? 'text-red-400' :
                            log.includes('OK') || log.includes('TERMINÉ') || log.includes('PRÊT') || log.includes('✓') ? 'text-green-400' :
                            log.includes('COMPENDIUM') || log.includes('📚') ? 'text-purple-400' :
                            log.includes('ANGLES') || log.includes('🎯') ? 'text-amber-400' :
                            log.includes('RECHERCHE') || log.includes('⚠') ? 'text-yellow-400' :
                            log.includes('PIPELINE V2') ? 'text-sky-400' :
                            'text-slate-500'
                          }`}>{log}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Generate Button */}
            <div className="space-y-3">
              {/* Articles supplémentaires - Composant extrait */}
              <ExtraSlots
                extraSlots={extraSlots}
                showExtraSlots={showExtraSlots}
                onToggleExtraSlots={(show) => { 
                  if (show) { 
                    setShowExtraSlots(true); 
                    if (extraSlots.length === 0) addExtraSlot(); 
                  } else { 
                    setShowExtraSlots(false); 
                    setExtraSlots([]); 
                  } 
                }}
                onAddExtraSlot={addExtraSlot}
                onRemoveExtraSlot={removeExtraSlot}
                onUpdateExtraSlot={updateExtraSlot}
              />

              {/* Options image - Composant extrait */}
              <ImageModeSelector
                imageMode={imageMode}
                imageSearchKeyword={imageSearchKeyword}
                onImageModeChange={setImageMode}
                onImageSearchKeywordChange={setImageSearchKeyword}
              />
              
              {/* Bouton Générer avec statut d'activité intégré */}
              <div className="mt-6 space-y-0" data-testid="generate-btn-wrapper">
                {/* Bandeau statut — utilise generationLock (verrou MongoDB) comme source de vérité */}
                <div className={`rounded-t-lg px-4 py-2 flex items-center justify-center gap-2.5 text-sm font-semibold ${
                  generating
                    ? 'bg-sky-500/20 border border-b-0 border-sky-500/40 text-sky-300'
                    : generationLock.locked
                    ? 'bg-red-500/20 border border-b-0 border-red-500/40 text-red-300'
                    : jobsActivity?.recently_completed
                    ? 'bg-amber-500/15 border border-b-0 border-amber-500/40 text-amber-300'
                    : 'bg-emerald-500/15 border border-b-0 border-emerald-500/40 text-emerald-300'
                }`}>
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    generating
                      ? 'bg-sky-400 animate-pulse'
                      : generationLock.locked
                      ? 'bg-red-400 animate-pulse'
                      : jobsActivity?.recently_completed
                      ? 'bg-amber-400'
                      : 'bg-emerald-400'
                  }`} />
                  {generating ? (
                    <span>VOTRE CRÉATION EST EN COURS</span>
                  ) : generationLock.locked ? (
                    <span>
                      GÉNÉRATION EN COURS
                      {generationLock.user_name && (
                        <span className="font-normal"> par {generationLock.user_name}</span>
                      )}
                      {generationLock.total_sites > 0 && (
                        <span className="font-normal opacity-70 ml-1">
                          ({generationLock.current_site || 0}/{generationLock.total_sites} sites)
                        </span>
                      )}
                      {generationLock.current_step && (
                        <span className="font-normal opacity-70 ml-1">
                          — {generationLock.current_step === 'generating_text' ? 'Génération texte' 
                            : generationLock.current_step === 'generating_image' ? 'Génération image'
                            : generationLock.current_step === 'uploading' ? 'Upload'
                            : generationLock.current_step}
                        </span>
                      )}
                      <span className="font-normal opacity-70 ml-1">— patientez</span>
                    </span>
                  ) : jobsActivity?.recently_completed ? (
                    <span>
                      CRÉATION AUTORISÉE
                      <span className="font-normal opacity-70 ml-1.5">
                        — dernière terminée il y a {Math.floor(jobsActivity.last_completed_seconds_ago / 60)} min
                      </span>
                    </span>
                  ) : (
                    <span>CRÉATION AUTORISÉE</span>
                  )}
                </div>
                {/* Bouton */}
                <button
                  onClick={handleGenerate}
                  disabled={generating || generationLock.locked || selectedSites.length === 0}
                  className={`w-full uppercase tracking-widest font-bold text-lg transition-all disabled:opacity-50 py-5 flex items-center justify-center gap-2 ${
                    generating
                      ? 'rounded-b-lg bg-sky-500/10 border-2 border-t-0 border-sky-500/40 text-sky-400'
                      : generationLock.locked
                      ? 'rounded-b-lg bg-red-500/10 border-2 border-t-0 border-red-500/40 text-red-400'
                      : jobsActivity?.recently_completed
                      ? 'rounded-b-lg bg-amber-500/10 border-2 border-t-0 border-amber-500/40 text-amber-300 hover:bg-amber-500/15'
                      : 'rounded-b-lg bg-emerald-500/10 border-2 border-t-0 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15'
                  }`}
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Génération en cours...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      <span>
                        Générer {(() => {
                          const total = selectedSites.length + extraSlots.filter(s => s.prompt.trim()).length;
                          return total > 0 ? `${total} article${total > 1 ? 's' : ''}` : '';
                        })()}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ALERTE SCRAPING ÉCHOUÉ */}
        {scrapeAlert && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="scrape-alert-overlay">
            <div className="bg-slate-900 border-2 border-red-500/60 rounded-xl p-6 max-w-md mx-4 shadow-2xl shadow-red-500/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-red-400 uppercase tracking-wide">Alerte : aucune information extraite des sources</h3>
              </div>
              <p className="text-sm text-slate-300 mb-6">
                Le scraping des URLs fournies n&apos;a retourné aucun contenu exploitable. L&apos;article sera généré uniquement à partir de votre dictée et des compléments textuels.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => handleScrapeDecision('cancel')}
                  className="px-5 py-2.5 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors text-sm font-semibold"
                  data-testid="scrape-alert-cancel"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleScrapeDecision('continue')}
                  className="px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white transition-colors text-sm font-semibold"
                  data-testid="scrape-alert-continue"
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Articles générés - s'affiche dès qu'il y a des articles OU pendant la génération */}
        {(generatedArticles && Object.keys(generatedArticles).length > 0) && (
          <div id="generated-articles" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-orbitron uppercase text-green-400 mb-2">
                Articles generés
                {generating && (
                  <span className="text-lg text-sky-400 font-normal ml-3">
                    ({Object.keys(generatedArticles).length}/{jobStatus?.total_sites || '?'})
                  </span>
                )}
              </h2>
              <p className="text-slate-400">
                {generating ? 'Les articles apparaissent au fur et à mesure...' : 'Relisez et choisissez votre action'}
              </p>
              {totalCost !== null && !generating && (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-slate-900/60 border border-slate-700 rounded-full">
                  <span className="text-xs text-slate-500 uppercase">Coût génération</span>
                  <span className="text-lg font-bold text-sky-400">{totalCost.toFixed(2)} €</span>
                </div>
              )}
            </div>

            {/* Récapitulatif de la demande */}
            <Card className="bg-slate-900/30 border-slate-800/50 mb-4">
              <CardContent className="p-4">
                <details className="group" open>
                  <summary className="cursor-pointer text-xs text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-2 select-none">
                    <FileText className="w-3.5 h-3.5" />
                    Récapitulatif de la demande
                    <span className="text-slate-700 group-open:rotate-90 transition-transform">›</span>
                  </summary>
                  <div className="mt-3 space-y-3">
                    {/* Sujet / Dictée */}
                    {dicteeText && (
                      <div>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wider">Sujet</span>
                        <p className="text-xs text-slate-300 mt-0.5 whitespace-pre-wrap line-clamp-4 bg-slate-950/40 rounded p-2 border border-slate-800/50">{dicteeText}</p>
                      </div>
                    )}
                    {pastedText && (
                      <div>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wider">Texte fourni</span>
                        <p className="text-xs text-slate-300 mt-0.5 whitespace-pre-wrap line-clamp-4 bg-slate-950/40 rounded p-2 border border-slate-800/50">{pastedText}</p>
                      </div>
                    )}
                    {/* Compléments */}
                    {(additionalNotes || additionalUrls || primarySeoKeyword) && (
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {additionalNotes && (
                          <div className="flex-1 min-w-[200px]">
                            <span className="text-[10px] text-slate-600 uppercase tracking-wider">Compléments</span>
                            <p className="text-xs text-slate-400 mt-0.5 whitespace-pre-wrap line-clamp-3">{additionalNotes}</p>
                          </div>
                        )}
                        {additionalUrls && (
                          <div className="flex-1 min-w-[200px]">
                            <span className="text-[10px] text-slate-600 uppercase tracking-wider">Sources</span>
                            <div className="mt-0.5 space-y-0.5">
                              {additionalUrls.split('\n').filter(u => u.trim()).map((url, i) => (
                                <p key={i} className="text-[10px] text-sky-500/70 font-mono truncate">{url.trim()}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {primarySeoKeyword && (
                          <div>
                            <span className="text-[10px] text-slate-600 uppercase tracking-wider">SEO</span>
                            <p className="text-xs text-sky-400 font-mono mt-0.5">{primarySeoKeyword}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Paramètres par site */}
                    {orderedSites.length > 0 && (
                      <div>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wider mb-1.5 block">Paramètres par site</span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {orderedSites.map(site => {
                            const title = siteTitles[site.id];
                            const rewrite = siteRewritePrompts[site.id];
                            const override = sitePrompts[site.id];
                            if (!title && !rewrite && !override) {
                              return (
                                <div key={site.id} className="flex items-center gap-1.5 text-[10px] text-slate-600 px-2 py-1 rounded bg-slate-950/30 border border-slate-800/30">
                                  <img src={getFaviconUrl(site.url)} alt="" className="w-3.5 h-3.5 rounded" onError={(e) => e.target.style.display = 'none'} />
                                  <span className="text-slate-400">{site.name}</span>
                                  <span className="text-slate-700 italic ml-auto">défaut</span>
                                </div>
                              );
                            }
                            return (
                              <div key={site.id} className="px-2.5 py-2 rounded bg-slate-950/40 border border-slate-800/40">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <img src={getFaviconUrl(site.url)} alt="" className="w-3.5 h-3.5 rounded" onError={(e) => e.target.style.display = 'none'} />
                                  <span className="text-xs text-slate-300 font-semibold">{site.name}</span>
                                </div>
                                {title && (
                                  <p className="text-[10px] text-amber-400/80 font-mono truncate">Titre : {title}</p>
                                )}
                                {rewrite && (
                                  <p className="text-[10px] text-sky-400/70 mt-0.5 line-clamp-2">Détails : {rewrite}</p>
                                )}
                                {override && (
                                  <p className="text-[10px] text-red-400/60 mt-0.5 line-clamp-1">Override prompt actif</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              </CardContent>
            </Card>

            {/* Sources utilisées pour l'enrichissement web */}
            {jobStatus?.enrich_sources && jobStatus.enrich_sources.length > 0 && (
              <Card className="bg-slate-900/30 border-emerald-700/30 mb-4" data-testid="enrich-sources-card">
                <CardContent className="p-4">
                  <details className="group" open>
                    <summary className="cursor-pointer text-xs text-emerald-400 uppercase tracking-wider font-semibold flex items-center gap-2 select-none">
                      <Globe className="w-3.5 h-3.5" />
                      Sources d&apos;enrichissement ({jobStatus.enrich_sources.length})
                      <span className="text-slate-700 group-open:rotate-90 transition-transform">›</span>
                    </summary>
                    <p className="text-[10px] text-slate-500 mt-2 mb-3">
                      Articles récents (&lt; 7 jours) identifiés et transmis à l&apos;IA pour enrichir le contexte.
                    </p>
                    <ul className="space-y-2">
                      {jobStatus.enrich_sources.map((src) => {
                        const flag = { fr: '🇫🇷', en: '🇬🇧', it: '🇮🇹', de: '🇩🇪', es: '🇪🇸' }[src.lang] || '🌐';
                        return (
                          <li
                            key={src.index}
                            className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-950/40 border border-slate-800/60 hover:border-emerald-500/40 transition-colors"
                            data-testid={`enrich-source-${src.index}`}
                          >
                            <span className="text-xs font-mono text-emerald-400/80 mt-0.5 shrink-0 w-6">#{src.index}</span>
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${src.domain}&sz=32`}
                              alt=""
                              className="w-4 h-4 rounded mt-0.5 shrink-0"
                              onError={(e) => (e.target.style.display = 'none')}
                            />
                            <div className="flex-1 min-w-0">
                              <a
                                href={src.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-slate-200 hover:text-emerald-300 font-medium line-clamp-1"
                                title={src.title}
                              >
                                {src.title || src.domain}
                              </a>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-[10px] text-slate-500">{src.publisher}</span>
                                <span className="text-[10px] text-slate-700">•</span>
                                <span className="text-[10px] text-slate-500 font-mono">{src.domain}</span>
                                <span className="text-[10px] text-slate-700">•</span>
                                <span className="text-[10px] text-slate-500">{src.date}</span>
                                <span className="text-[10px] text-slate-700">•</span>
                                <span className="text-[10px] leading-none">{flag}</span>
                                <span className="text-[10px] text-slate-700">•</span>
                                <span className="text-[10px] text-slate-600">{(src.char_count || 0).toLocaleString()} chars</span>
                              </div>
                              {src.query_used && (
                                <p className="text-[10px] text-slate-600 mt-1 italic truncate" title={src.query_used}>
                                  Requête : « {src.query_used} »
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                </CardContent>
              </Card>
            )}

            {/* Mini-barre de progression compacte pendant la génération */}
            {generating && jobStatus && (
              <Card className="bg-slate-900/50 border-sky-500/30 mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                      <span className="text-sm font-mono text-sky-400">
                        Génération en cours ({jobStatus.completed_sites}/{jobStatus.total_sites})
                      </span>
                    </div>
                    {/* Timer compact et heartbeat */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-500 tabular-nums">{formatElapsedTime(elapsedTime)}</span>
                      <div 
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          lastPollTime && (Date.now() - lastPollTime) < 3000 
                            ? 'bg-emerald-500 animate-pulse' 
                            : 'bg-sky-500'
                        }`}
                        title="Indicateur de connexion"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {jobStatus.sites_status?.map((site, index) => {
                      const isActive = site.status !== 'completed' && site.status !== 'error' && site.status !== 'pending';
                      return (
                      <div 
                        key={site.site_id} 
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-all ${
                          site.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                          site.status === 'error' ? 'bg-red-500/10 text-red-400' :
                          site.status === 'pending' ? 'bg-slate-800 text-slate-500' :
                          'bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/30'
                        }`}
                      >
                        {site.status === 'completed' ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : site.status === 'error' ? (
                          <AlertCircle className="w-3 h-3" />
                        ) : site.status === 'pending' ? (
                          <Clock className="w-3 h-3" />
                        ) : (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        )}
                        <span className="truncate">{site.site_name}</span>
                      </div>
                      );
                    })}
                  </div>
                  {/* Message dynamique compact */}
                  {(() => {
                    const processing = jobStatus.sites_status?.find(s => s.status !== 'completed' && s.status !== 'error' && s.status !== 'pending');
                    if (processing) {
                      return (
                        <p className="text-[10px] text-slate-500 mt-2 font-mono">
                          {processing.status === 'generating_text' ? `Rédaction pour ${processing.site_name}...` :
                           processing.status === 'generating_image' ? `Image pour ${processing.site_name}...` :
                           processing.status === 'enriching' ? 'Recherche de sources...' :
                           processing.status === 'scraping' ? 'Analyse des URLs...' :
                           `Traitement de ${processing.site_name}...`}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Résumé du Cluster SEO (basé sur les articles rechargés après injection backend) */}
            {!generating && clusterMode && (() => {
              const allDetails = [];
              for (const [siteId, article] of Object.entries(generatedArticles)) {
                if (siteId.startsWith('extra_')) continue;
                const details = article?.cluster_links_details || [];
                for (const d of details) {
                  allDetails.push({ ...d, from_site_id: siteId });
                }
              }
              if (allDetails.length === 0) return null;
              const successCount = allDetails.filter(d => d.success).length;
              return (
                <Card className="bg-slate-900/40 border-emerald-500/30 mb-6" data-testid="cluster-summary-card">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Cluster SEO — {successCount}/{allDetails.length} liens injectés
                      </h3>
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {allDetails.map((d) => {
                        const fromSite = sites.find(s => s.id === d.from_site_id);
                        const toSite = sites.find(s => s.id === d.to_site_id);
                        const uniqueKey = `${d.from_site_id}-${d.to_site_id}-${d.anchor || 'no-anchor'}`;
                        return (
                          <div
                            key={uniqueKey}
                            className={`flex items-center gap-2 p-2 rounded border text-xs ${
                              d.success
                                ? 'bg-emerald-500/10 border-emerald-500/30'
                                : 'bg-slate-800/50 border-slate-700'
                            }`}
                          >
                            <span className="font-medium text-slate-300">{fromSite?.name || d.from_site_id}</span>
                            <span className="text-emerald-400">→</span>
                            <span className="font-medium text-slate-300">{toSite?.name || d.to_site_id}</span>
                            <span className="text-slate-500 mx-1">|</span>
                            <span className="text-emerald-300 italic truncate flex-1" title={d.anchor || d.reason}>
                              {d.success ? `"${d.anchor}"` : (d.reason || 'non injecté')}
                            </span>
                            {d.success ? (
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Publication multi-sites groupée — seulement quand génération terminée, exclure les extras */}
            {!generating && Object.entries(generatedArticles).filter(([id]) => !id.startsWith('extra_')).length > 1 && (
              <Card className="bg-slate-900/40 border-purple-500/30 mb-6">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">Publication groupée ({Object.entries(generatedArticles).filter(([id]) => !id.startsWith('extra_')).length} sites)</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-400 whitespace-nowrap">Délai entre sites :</label>
                      <Input
                        type="number"
                        min="0"
                        max="1440"
                        value={publishDelay}
                        onChange={(e) => setPublishDelay(parseInt(e.target.value) || 0)}
                        className="w-20 h-8 bg-slate-950/50 border-slate-700 text-slate-200 text-sm"
                        data-testid="publish-delay-input"
                      />
                      <span className="text-xs text-slate-500">min</span>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button 
                        onClick={() => handlePublishAll('draft')} 
                        disabled={publishing}
                        className="bg-slate-800/50 hover:bg-slate-800 text-slate-300 border border-slate-700 text-sm h-8 px-3"
                        data-testid="publish-all-draft-btn"
                      >
                        Tous en brouillon
                      </Button>
                      <Button 
                        onClick={() => handlePublishAll('publish')} 
                        disabled={publishing}
                        className="bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/50 text-sm h-8 px-3"
                        data-testid="publish-all-live-btn"
                      >
                        Tous en ligne {publishDelay > 0 ? `(${publishDelay}min d'écart)` : ''}
                      </Button>
                    </div>
                  </div>
                  {publishDelay > 0 && (
                    <p className="text-xs text-slate-500">
                      Site 1 : immédiat — {Object.entries(generatedArticles).slice(1).map(([siteId], i) => {
                        const name = sites.find(s => s.id === siteId)?.name || siteId;
                        return `${name} : +${publishDelay * (i + 1)}min`;
                      }).join(' — ')}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Articles générés - Utilisation du composant extrait */}
            {Object.entries(generatedArticles).map(([siteId, rawArticleData]) => (
              <GeneratedArticleCard
                key={siteId}
                siteId={siteId}
                rawArticleData={rawArticleData}
                sites={sites}
                publishedArticles={publishedArticles}
                publishing={publishing}
                uploadingCustomImage={uploadingCustomImage}
                onUpdateArticle={updateArticleData}
                onCustomImageUpload={handleCustomImageUpload}
                onPublish={handlePublish}
              />
            ))}

            {/* ================================================================
                SECTION : CONTINUER CETTE MISSION (Régénération)
                ================================================================ */}
            {!generating && Object.keys(generatedArticles || {}).length > 0 && (
              <RegenerationPanel
                showPanel={showRegenPanel}
                onTogglePanel={() => setShowRegenPanel(!showRegenPanel)}
                compendiumStats={jobStatus?.compendium_stats || {}}
                generatedArticles={generatedArticles}
                sites={sites}
                regenSites={regenSites}
                onRegenSiteChange={(siteId, updates) => setRegenSites(prev => ({
                  ...prev,
                  [siteId]: { ...prev[siteId], ...updates }
                }))}
                onRegenerate={handleRegenerate}
                regenerating={regenerating}
                verboseLog={jobStatus?.verbose_log}
              />
            )}

            {/* Boutons de navigation */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  setGeneratedArticles(null);
                  setTotalCost(null);
                  setUploadedAudio(null);
                  setAdditionalNotes('');
                  setPublishedArticles({});
                  setSiteTitles({});
                  setExtraSlots([]);
                  setShowExtraSlots(false);
                  setPrimarySeoKeyword('');
                  setSecondaryKeywords('');
                  setCurrentJobId(null);
                  setJobStatus(null);
                  setRegenSites({});
                  setShowRegenPanel(false);
                  completionNotifiedRef.current = false;
                  sessionStorage.removeItem(storageKey);
                }}
                className="flex-1 bg-slate-800/50 hover:bg-slate-800 text-slate-400 border border-slate-700 font-semibold py-4 rounded-lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Créer un nouvel article
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="flex-1 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/50 font-semibold py-4 rounded-lg"
              >
                Retour au centre de commande
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateArticle;
